/**
 * User Home Page
 * Main dashboard for regular users (non-agency)
 * Protected route - requires authentication
 * 
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import confetti from 'canvas-confetti';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from 'recharts';
import {
  Home,
  User,
  FolderPlus,
  Wallet,
  Star,
  Smartphone,
  BarChart3,
  Sparkles,
  Rocket,
  Search,
  Bell,
  ChevronsRight,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Mail,
  Phone,
  TrendingUp,
  Settings,
  HelpCircle,
  ExternalLink,
  MapPin,
  Calendar,
  Flame,
  ImagePlus,
  CircleDollarSign,
  Instagram,
  Images,
  Mic2,
  BookOpen,
  Award,
  Share2,
  UserCircle,
  Edit,
  CheckCircle2,
  Menu,
  FileEdit,
  Building2,
  Zap,
  ShieldCheck,
  Clock3,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useBookingFormsStore } from '../../stores/bookingFormsStore';
import { AuthService } from '../../services/authService';
import { cn } from '../../utils/cn';
import { StatisticsPage } from './StatisticsPage';
import { SettingsPage } from './SettingsPage';
import { ContactUsPage } from './ContactUsPage';
import { FAQPage } from './FAQPage';
import { WalletPage } from './WalletPage';
import { MembershipPage } from './MembershipPage';
import { ComparePlansPage } from './ComparePlansPage';
import { JobsPage } from './JobsPage';
import { ArtistChatPage } from './jobs/ArtistChatPage';
import { JobDetailPage } from './JobDetailPage';
import { ExpressBookingPage } from './ExpressBookingPage';
import { ExpressBookingEnablePage } from './ExpressBookingEnablePage';
import { BookingFillPage } from './jobs/BookingFillPage';
import { BookingFillPage as BookingFillPageEB } from './jobs/BookingFillPageEB';
import { ProfileGalleryPage } from '../profile/ProfileGalleryPage';
import { ProfilePersonalDetailsPage } from '../profile/ProfilePersonalDetailsPage';
import { ProfilePerformanceDetailsPage } from '../profile/ProfilePerformanceDetailsPage';
import { ProfileUspPage } from '../profile/ProfileUspPage';
import { ProfileStoryPage } from '../profile/ProfileStoryPage';
import { ProfileAwardsAchievementsPage } from '../profile/ProfileAwardsAchievementsPage';
import { ProfilePastPerformancesPage } from '../profile/ProfilePastPerformancesPage';
import { ProfileSocialMediaPage } from '../profile/ProfileSocialMediaPage';
import { ProfileEventEssentialsPage } from '../profile/ProfileEventEssentialsPage';
import { EVENT_TYPES } from '../../constants/eventTypes';
import { GlobalSearch } from '../../components/search/GlobalSearch';
import { AssistantMainView } from '../../components/ai/AssistantMainView';
import { AssistantMiniChat } from '../../components/ai/AssistantMiniChat';
import type { AssistantUiContext } from '../../stores/assistantStore';
import profileWave from '../../assets/images/profile-wave.png';
import expressCardBg from '../../assets/images/express-card-bg.png';
import { EXPRESS_BOOKING_GIF_URL } from '../../constants/expressBookingAssets';
import { isSingerOrLiveBandCategory } from '../../data/membershipPlansData';
import starcoinAnimation from '../../assets/videos/starcoin-animation.gif';
import levelupCrown from '../../assets/videos/levelup-crown.gif';
import downloadAppStoreBadge from '../../assets/images/download-app-store.svg';
import downloadGooglePlayBadge from '../../assets/images/download-google-play.svg';
import eliteGroupBackground from '../../assets/images/elite-group-background.svg';
import { BottomNav } from '../../components/layout/BottomNav';
import { Sheet } from '../../components/ui/Sheet';
import { LeftSheet } from '../../components/ui/LeftSheet';
import { LoggingOutOverlay } from '../../components/ui/LoggingOutOverlay';
import { BackButton } from '../../components/navigation/BackButton';
import { Button } from '../../components/ui/Button';
import { Loader } from '../../components/ui';
import { ProfilePicWithProgress } from '../../components/ui/ProfilePicWithProgress';
import { ProfilePhotoModal } from '../../components/features/ProfilePhotoModal';
import { ContainerTextFlip } from '../../components/ui/container-text-flip';
import { SparkleMaskedName } from '../../components/ui/SparkleMaskedName';
import { useArtistDetailsStore } from '../../stores/artistDetailsStore';
import { usePersonalDetailsStore } from '../../stores/personalDetailsStore';
import type { ArtistDetailsJob } from '../../types/artistDetails';
import { storageManager } from '../../storage/storageManager';
import { STORAGE_KEYS } from '../../storage/storageKeys';
import {
  buildExpressEventCandidates,
  formatCountdown,
  getActiveExpressEvent,
  type ActiveExpressEvent,
} from '../../services/eventOngoingService';
import { playEventEdgeSound } from '../../services/eventSoundService';
import { syncExpressEventNotifications } from '../../services/eventNotificationService';
import toast from 'react-hot-toast';
import apiClient from '../../api/client';
import { API_ENDPOINTS } from '../../api/endpoints';
import {
  deriveProfileCompletion,
  checklistBlockNeedsAttention,
  segmentNeedsAttention,
  isProfileCompletionFullyComplete,
  getIncompletePriorityTasks,
  PROFILE_EVENT_PRICING_SEGMENT_INDEX,
  PROFILE_PHOTO_SEGMENT_INDEX,
  type ProfileCompletionSegmentId,
} from '../../constants/profileCompletionSegments';
import {
  ProfileCompletionListContent,
  ProfileCompletionMiniBar,
  ProfileIncompleteCornerPing,
} from '../../components/profile/ProfileCompletionBlocksControl';
import { PERFORMANCE_CITIES } from '../../components/expressBooking/performanceCities';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  hasChevron?: boolean;
}

type EliteMember = {
  professional_name: string;
  slug: string;
  profile_pic: string;
  location: string;
};

type EliteMembersResponse = {
  success: boolean;
  data: EliteMember[];
};

type CategoryPerformancePeriod = '7d' | '30d' | '90d' | '180d' | '365d';
type CategoryPerformanceMonthlyBooking = {
  month: string;
  label: string;
  booking_count: number;
};

type CategoryPerformanceData = {
  period: string;
  start_date: string;
  end_date: string;
  monthly_bookings: CategoryPerformanceMonthlyBooking[];
  total_bookings: number;
};

type CategoryPerformanceResponse = {
  success: boolean;
  data: CategoryPerformanceData;
};

type RegionalRankArtist = {
  artist_id: number;
  artist_name: string;
  slug: string;
  profile_pic: string | null;
  bookings_count: number;
  pageviews_count: number;
  score: number;
  rank: number;
  category: string | null;
  plan: string | null;
};

type RegionalRankData = {
  period: string;
  start_date: string;
  end_date: string;
  top_artists: RegionalRankArtist[];
};

type RegionalRankResponse = {
  success: boolean;
  data: RegionalRankData;
};

const CATEGORY_PERFORMANCE_PERIOD_OPTIONS: { label: string; value: CategoryPerformancePeriod }[] = [
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
  { label: '180d', value: '180d' },
  { label: '1 year', value: '365d' },
];

const ARTIST_PORTAL_BASE = 'https://starclinch.com';
const FREE_PLAN_PROFILE_UPGRADE_COPY: Record<string, string> = {
  '/profile/gallery':
    'Want more control over how your gallery looks? Unlock more uploads and curated layouts!',
  '/profile/performance-details':
    'Want your performance details to look more professional? Unlock expanded details and a premium display!',
  '/profile/story':
    'Your journey deserves more than just words. Add a featured image to make it unforgettable!',
  '/profile/awards-achievements':
    'Turn your achievements into a visual journey. Unlock the timeline showcase for your milestones.',
  '/profile/past-performances':
    'Your performances deserve more than a table. Showcase them with a premium layout.',
};

const priorityTaskIconClass =
  'h-4 w-4 text-[#E5E5EA] group-hover:text-[#F33363] transition-colors';

function renderPriorityTaskIcon(segmentId: ProfileCompletionSegmentId) {
  switch (segmentId) {
    case 'gallery':
      return <Images className={priorityTaskIconClass} />;
    case 'personal':
      return <User className={priorityTaskIconClass} />;
    case 'performance':
      return <Mic2 className={priorityTaskIconClass} />;
    case 'usp':
      return <Star className={priorityTaskIconClass} />;
    case 'story':
      return <BookOpen className={priorityTaskIconClass} />;
    case 'achievements':
      return <Award className={priorityTaskIconClass} />;
    case 'social':
      return <Share2 className={priorityTaskIconClass} />;
    case 'event_pricing':
      return <CircleDollarSign className={priorityTaskIconClass} />;
    case 'profile_photo':
      return <UserCircle className={priorityTaskIconClass} />;
    default:
      return <Sparkles className={priorityTaskIconClass} />;
  }
}

function toTwoWordName(name: string | null | undefined): string {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return 'Artist Name';
  return trimmed.split(/\s+/).slice(0, 2).join(' ');
}

// NOTE: This MUST be a module-level component (not declared inside UserHome),
// otherwise it will be re-created on every UserHome render and will unmount/remount,
// which can re-trigger BookingFillPageEB effects and spam APIs.
const BookingFillRoute: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const routeLocation = useLocation();

  const ensureFetched = useBookingFormsStore((s) => s.ensureFetched);
  const hasFetchedInSession = useBookingFormsStore((s) => s.hasFetchedInSession);
  const toFillLoading = useBookingFormsStore((s) => s.toFillLoading);
  const submittedLoading = useBookingFormsStore((s) => s.submittedLoading);
  const error = useBookingFormsStore((s) => s.error);
  const toFill = useBookingFormsStore((s) => s.toFill);
  const submitted = useBookingFormsStore((s) => s.submitted);

  const formFromState = (routeLocation.state as { form?: { artist_id?: number } } | null)
    ?.form;
  const isExpressFromState =
    typeof formFromState?.artist_id === 'number' && formFromState.artist_id === 0;

  useEffect(() => {
    if (isExpressFromState) return;
    void ensureFetched();
  }, [ensureFetched, isExpressFromState]);

  if (isExpressFromState) return <BookingFillPageEB />;

  const numId = Number(formId);
  const foundNormal =
    Number.isFinite(numId) &&
    (toFill.some((f) => f.id === numId) || submitted.some((f) => f.id === numId));

  if (foundNormal) return <BookingFillPage />;
  if (error) return <BookingFillPageEB />;

  if (!hasFetchedInSession || toFillLoading || submittedLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[60vh]">
        <Loader className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return <BookingFillPageEB />;
};

export const UserHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { data: artistDetails, fetchArtistDetails } = useArtistDetailsStore();
  const personalDetails = usePersonalDetailsStore((s) => s.data);
  const ensurePersonalDetailsFetched = usePersonalDetailsStore((s) => s.ensureFetched);
  const bookingFormsToFill = useBookingFormsStore((s) => s.toFill);
  const ensureBookingFormsFetched = useBookingFormsStore((s) => s.ensureFetched);
  const bookingFormsSubmitted = useBookingFormsStore((s) => s.submitted);
  const [activeNav, setActiveNav] = useState<string>('home');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isWebsitePlanSheetOpen, setIsWebsitePlanSheetOpen] = useState(false);
  const [isProfilePhotoModalOpen, setIsProfilePhotoModalOpen] = useState(false);
  const profileCompletionBarRef = useRef<HTMLDivElement | null>(null);
  const profileCompletionDesktopPanelRef = useRef<HTMLDivElement | null>(null);
  const [profileCompletionHover, setProfileCompletionHover] = useState(false);
  const [profileCompletionPanelPos, setProfileCompletionPanelPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [profileCompletionMobileOpen, setProfileCompletionMobileOpen] = useState(false);
  const profileCompletionLeaveTimerRef = useRef<number | null>(null);
  const [isPrimaryContactEditOpen, setIsPrimaryContactEditOpen] = useState(false);
  const [isSecondaryContactEditOpen, setIsSecondaryContactEditOpen] = useState(false);
  const [activeContactTab, setActiveContactTab] = useState<'email' | 'phone'>('email');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isFirstTimeWelcome, setIsFirstTimeWelcome] = useState(false);
  const [isMembershipNudgeOpen, setIsMembershipNudgeOpen] = useState(false);
  const [membershipNudgeVariant, setMembershipNudgeVariant] = useState<
    'live_free' | 'incomplete_free' | 'on_review'
  >('live_free');

  // Elite Members list (rotating every few seconds)
  const [eliteMembers, setEliteMembers] = useState<EliteMember[]>([]);
  const [eliteMemberIndex, setEliteMemberIndex] = useState(0);
  const [eliteIsFading, setEliteIsFading] = useState(false);
  const [eliteIsHoverPaused, setEliteIsHoverPaused] = useState(false);

  // Category Performance (graph card on User Home)
  const [categoryPerformancePeriod, setCategoryPerformancePeriod] =
    useState<CategoryPerformancePeriod>('365d');
  const [categoryPerformance, setCategoryPerformance] = useState<CategoryPerformanceData | null>(null);
  const [categoryPerformanceLoading, setCategoryPerformanceLoading] = useState(false);
  const [categoryPerformanceError, setCategoryPerformanceError] = useState<string | null>(null);
  const [regionalRankData, setRegionalRankData] = useState<RegionalRankData | null>(null);
  const [ongoingEventStripBottom, setOngoingEventStripBottom] = useState(134);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const isKeyboardOpenRef = useRef(false);
  const upgradeToastShownPathRef = useRef<string | null>(null);
  const upgradeToastTimerRef = useRef<number | null>(null);
  const closeKeyboardTimerRef = useRef<number | null>(null);
  const navItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mainRef = useRef<HTMLDivElement | null>(null);

  const userInfo = artistDetails?.user_info;
  const isNativeApp = Capacitor.isNativePlatform();
  const subscriptions = artistDetails?.subscriptions;
  const userName = userInfo?.professional_name || user?.name || 'Artist Name';
  const subscriptionNameLabel = String(subscriptions?.subscription_name ?? '');
  const planDisplayName =
    subscriptionNameLabel.toLowerCase() === 'free'
      ? 'Limited Plan'
      : subscriptionNameLabel || 'Limited Plan';
  const subscriptionExpireLabel = useMemo(() => {
    const raw = subscriptions?.expiring_on?.trim();
    if (!raw) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (!match) return null;
    const [, year, month, day] = match;
    return `${day}-${month}-${year}`;
  }, [subscriptions?.expiring_on]);
  const profileStatus = String(userInfo?.profile_status ?? '');
  const queueNumberValue = userInfo?.queue;
  const queueNumberDisplay = useMemo(() => {
    if (queueNumberValue === null || queueNumberValue === undefined) return null;
    if (typeof queueNumberValue === 'string' && queueNumberValue.trim() === '') return null;
    const n = typeof queueNumberValue === 'number' ? queueNumberValue : Number(queueNumberValue);
    return Number.isFinite(n) ? n : null;
  }, [queueNumberValue]);
  const profilePic = userInfo?.profile_pic;
  const fullProfilePercent = userInfo?.profile_completion?.full_profile ?? 0;
  const artistSlug = userInfo?.artist_slug ?? '';
  const artistPortalUrl = artistSlug ? `${ARTIST_PORTAL_BASE}/${artistSlug}` : `${ARTIST_PORTAL_BASE}/artist-portal`;
  const isLive = profileStatus.toLowerCase() === 'approved' || profileStatus.toLowerCase() === 'live';
  const artistCategoryName = artistDetails?.artist_category?.name ?? '';
  const showExpressBookingFeatures = isSingerOrLiveBandCategory(artistCategoryName);
  const expressBookingRoute = artistDetails?.eb_tnc ? '/express-booking' : '/express-booking-consent-form';
  const expressBookingNavPath = expressBookingRoute.replace(/^\//, '');
  const userType = String(artistDetails?.user_info?.type?.user_type ?? '').trim();
  const isAgencyUser = userType.toLowerCase() === 'agency';
  const isFreePlan = subscriptionNameLabel.toLowerCase() === 'free';
  const eventTrackerArtistKey = artistSlug || String(userInfo?.email || user?.email || user?.id || 'unknown');
  const freePlanProfileUpgradeMessage = FREE_PLAN_PROFILE_UPGRADE_COPY[location.pathname] ?? null;
  const showFreePlanProfileUpgradeStrip =
    isFreePlan && activeNav === 'profile' && Boolean(freePlanProfileUpgradeMessage);
  const [eventClockMs, setEventClockMs] = useState(() => Date.now());
  const [soundMarkersByEventKey, setSoundMarkersByEventKey] = useState<Record<string, { startPlayedAt?: number; endPlayedAt?: number }>>({});
  const [soundMarkersReady, setSoundMarkersReady] = useState(false);
  const previousEventClockMsRef = useRef<number>(Date.now());
  const activeExpressEvent = useMemo<ActiveExpressEvent | null>(() => {
    const candidates = buildExpressEventCandidates(bookingFormsSubmitted);
    return getActiveExpressEvent(candidates, eventClockMs);
  }, [bookingFormsSubmitted, eventClockMs]);
  const ongoingEventCountdown = useMemo(
    () => formatCountdown(activeExpressEvent?.remainingSeconds ?? 0),
    [activeExpressEvent?.remainingSeconds]
  );

  useEffect(() => {
    isKeyboardOpenRef.current = isKeyboardOpen;
  }, [isKeyboardOpen]);

  // Allow re-showing the upgrade toast on a new route entry.
  // Prevents repeated toasts on re-renders while staying on the same page.
  useEffect(() => {
    upgradeToastShownPathRef.current = null;
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle('keyboard-open', isKeyboardOpen);
    return () => {
      document.documentElement.classList.remove('keyboard-open');
    };
  }, [isKeyboardOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isIOSDevice = /iPhone|iPad|iPod/i.test(window.navigator.userAgent);

    const baselineViewportHeightRef = {
      current: window.visualViewport?.height ?? window.innerHeight,
    };

    const isTextInput = (el: Element | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;

      if (el.isContentEditable) return true;

      const tagName = el.tagName.toLowerCase();
      if (tagName === 'textarea') return true;
      if (tagName !== 'input') return false;
      const input = el as HTMLInputElement;
      return input.type !== 'button' && input.type !== 'checkbox' && input.type !== 'radio';
    };

    const updateKeyboardState = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        '--visual-viewport-height',
        `${viewportHeight}px`,
      );
      const diff = baselineViewportHeightRef.current - viewportHeight;

      // Hysteresis to reduce flicker when switching between inputs.
      const openThreshold = isIOSDevice ? 110 : 140;
      const closeThreshold = isIOSDevice ? 70 : 90;
      const shouldOpen = diff > openThreshold;
      const shouldClose = diff < closeThreshold;

      if (shouldOpen) {
        if (closeKeyboardTimerRef.current != null) {
          window.clearTimeout(closeKeyboardTimerRef.current);
          closeKeyboardTimerRef.current = null;
        }
        setIsKeyboardOpen(true);
        return;
      }

      if (isKeyboardOpenRef.current && shouldClose) {
        if (closeKeyboardTimerRef.current == null) {
          closeKeyboardTimerRef.current = window.setTimeout(() => {
            closeKeyboardTimerRef.current = null;
            const latestViewportHeight = window.visualViewport?.height ?? window.innerHeight;
            const latestDiff = baselineViewportHeightRef.current - latestViewportHeight;
            const latestShouldClose = latestDiff < closeThreshold;
            const hasFocusedInput = isTextInput(document.activeElement);
            if (latestShouldClose && !hasFocusedInput) {
              setIsKeyboardOpen(false);
            }
          }, 180);
        }
      }
    };

    const handleFocusIn = () => {
      updateKeyboardState();
    };

    const handleFocusOut = () => {
      window.setTimeout(updateKeyboardState, 0);
    };

    const handleResize = () => {
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      if (!isKeyboardOpenRef.current && viewportHeight > baselineViewportHeightRef.current) {
        baselineViewportHeightRef.current = viewportHeight;
      }
      updateKeyboardState();
    };

    const handleOrientationChange = () => {
      window.setTimeout(() => {
        baselineViewportHeightRef.current = window.visualViewport?.height ?? window.innerHeight;
        updateKeyboardState();
      }, 100);
    };

    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    updateKeyboardState();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (closeKeyboardTimerRef.current != null) {
        window.clearTimeout(closeKeyboardTimerRef.current);
        closeKeyboardTimerRef.current = null;
      }
    };
  }, []);

  // Free-plan upgrade toast: delay it slightly so the user can read the page content.
  // We show at most once per profile page visit and not while the keyboard is open.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (upgradeToastTimerRef.current != null) {
      window.clearTimeout(upgradeToastTimerRef.current);
      upgradeToastTimerRef.current = null;
    }

    if (!showFreePlanProfileUpgradeStrip || !freePlanProfileUpgradeMessage || isKeyboardOpen) return;

    const path = location.pathname;
    if (upgradeToastShownPathRef.current === path) return;

    upgradeToastTimerRef.current = window.setTimeout(() => {
      if (isKeyboardOpen) return;

      toast.custom(
        () => (
          <div
            className="mx-auto w-fit cursor-pointer rounded-2xl border border-[#3A3A3C]/80 bg-[linear-gradient(135deg,_#121214_0%,_#1A1A1F_40%,_#1E1117_100%)] px-4 py-3 shadow-[0_12px_36px_rgba(0,0,0,0.65)] transition-colors hover:border-[#F33363]/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363]/60"
            role="button"
            tabIndex={0}
            onClick={() => navigate('/membership')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navigate('/membership');
              }
            }}
          >
            <p className="text-[12px] leading-relaxed text-[#F2F2F7] text-center w-fit">
              {freePlanProfileUpgradeMessage}
            </p>
          </div>
        ),
        {
          duration: 7000,
          position: 'top-center',
        }
      );

      upgradeToastShownPathRef.current = path;
      upgradeToastTimerRef.current = null;
    }, 600);

    return () => {
      if (upgradeToastTimerRef.current != null) {
        window.clearTimeout(upgradeToastTimerRef.current);
        upgradeToastTimerRef.current = null;
      }
    };
  }, [showFreePlanProfileUpgradeStrip, freePlanProfileUpgradeMessage, isKeyboardOpen, location.pathname, navigate]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setEventClockMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const existing = await storageManager.get<Record<string, { startPlayedAt?: number; endPlayedAt?: number }>>(
        STORAGE_KEYS.EXPRESS_EVENT_SOUND_MARKERS(eventTrackerArtistKey)
      );
      if (cancelled) return;
      setSoundMarkersByEventKey(existing ?? {});
      setSoundMarkersReady(true);
    };
    void run();
    return () => {
      cancelled = true;
      setSoundMarkersReady(false);
    };
  }, [eventTrackerArtistKey]);

  useEffect(() => {
    if (!soundMarkersReady) return;
    void storageManager.set(
      STORAGE_KEYS.EXPRESS_EVENT_SOUND_MARKERS(eventTrackerArtistKey),
      soundMarkersByEventKey
    );
  }, [eventTrackerArtistKey, soundMarkersByEventKey, soundMarkersReady]);

  useEffect(() => {
    const now = eventClockMs;
    const previousNow = previousEventClockMsRef.current;
    previousEventClockMsRef.current = now;

    if (!soundMarkersReady) return;
    const candidates = buildExpressEventCandidates(bookingFormsSubmitted);
    const updates: Record<string, { startPlayedAt?: number; endPlayedAt?: number }> = {};
    let shouldCommit = false;

    for (const event of candidates) {
      const marker = soundMarkersByEventKey[event.eventKey] ?? {};
      const crossedStart = previousNow < event.startMs && now >= event.startMs && now < event.endMs;
      const crossedEnd = previousNow < event.endMs && now >= event.endMs;

      if (crossedStart && !marker.startPlayedAt) {
        void playEventEdgeSound();
        updates[event.eventKey] = { ...marker, startPlayedAt: now };
        shouldCommit = true;
      }

      if (crossedEnd && !marker.endPlayedAt) {
        void playEventEdgeSound();
        const nextMarker = updates[event.eventKey] ?? marker;
        updates[event.eventKey] = { ...nextMarker, endPlayedAt: now };
        shouldCommit = true;
      }
    }

    if (!shouldCommit) return;
    setSoundMarkersByEventKey((prev) => ({ ...prev, ...updates }));
  }, [bookingFormsSubmitted, eventClockMs, soundMarkersByEventKey, soundMarkersReady]);

  useEffect(() => {
    if (!soundMarkersReady) return;
    const candidates = buildExpressEventCandidates(bookingFormsSubmitted);
    void syncExpressEventNotifications({
      artistKey: eventTrackerArtistKey,
      candidates,
      soundMarkersByEventKey,
    });
  }, [bookingFormsSubmitted, eventTrackerArtistKey, soundMarkersByEventKey, soundMarkersReady]);

  useEffect(() => {
    if (!activeExpressEvent || isKeyboardOpen) return;

    const GAP_ABOVE_NAV = 6;
    let rafId: number | null = null;

    const updateStripPosition = () => {
      const bottomNavElement = document.querySelector<HTMLElement>('nav.bottom-nav-safe');
      const bottomNavRect = bottomNavElement?.getBoundingClientRect();
      const isBottomNavVisible =
        Boolean(bottomNavElement) &&
        Boolean(bottomNavRect) &&
        (bottomNavRect?.height ?? 0) > 0 &&
        window.getComputedStyle(bottomNavElement as HTMLElement).display !== 'none';

      if (!isBottomNavVisible || !bottomNavRect) {
        const desktopFallbackBottom = window.innerWidth >= 1024 ? 20 : 134;
        setOngoingEventStripBottom(desktopFallbackBottom);
        return;
      }

      const navTopFromViewportBottom = Math.max(0, window.innerHeight - bottomNavRect.top);
      setOngoingEventStripBottom(Math.round(navTopFromViewportBottom + GAP_ABOVE_NAV));
    };

    const schedule = () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateStripPosition);
    };

    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', schedule);
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', schedule);
    };
  }, [activeExpressEvent, isKeyboardOpen]);

  const profileCompletion = useMemo(() => deriveProfileCompletion(artistDetails), [artistDetails]);

  const showProfileCompletionBar = !isProfileCompletionFullyComplete(profileCompletion);

  const profilePhotoNeedsAttention = useMemo(
    () =>
      profileCompletion.isReady &&
      !profileCompletion.isIndeterminate &&
      segmentNeedsAttention(PROFILE_PHOTO_SEGMENT_INDEX, profileCompletion.segmentComplete),
    [profileCompletion]
  );

  const eventPricingNeedsAttention = useMemo(
    () =>
      profileCompletion.isReady &&
      !profileCompletion.isIndeterminate &&
      segmentNeedsAttention(PROFILE_EVENT_PRICING_SEGMENT_INDEX, profileCompletion.segmentComplete),
    [profileCompletion]
  );

  const handleProfileCompletionNavigate = useCallback(
    (path: string) => {
      navigate(path);
      setProfileCompletionMobileOpen(false);
      setProfileCompletionHover(false);
    },
    [navigate]
  );

  const artistLocationLabel = useMemo(() => {
    const loc = personalDetails?.location;
    if (!loc) return '';
    const one = Array.isArray(loc) ? loc[0] : loc;
    if (!one?.locality || !one?.city || !one?.state) return '';
    return `${one.locality}, ${one.city}, ${one.state}`;
  }, [personalDetails?.location]);

  const activeEliteMember = eliteMembers[eliteMemberIndex] ?? null;
  const regionalTopArtists = useMemo(
    () => [...(regionalRankData?.top_artists ?? [])].sort((a, b) => a.rank - b.rank),
    [regionalRankData]
  );
  const regionalPodium = useMemo(
    () => ({
      first: regionalTopArtists.find((artist) => artist.rank === 1) ?? null,
      second: regionalTopArtists.find((artist) => artist.rank === 2) ?? null,
      third: regionalTopArtists.find((artist) => artist.rank === 3) ?? null,
    }),
    [regionalTopArtists]
  );
  const regionalRankList = useMemo(
    () => regionalTopArtists.filter((artist) => artist.rank >= 4).slice(0, 3),
    [regionalTopArtists]
  );

  const handleEliteMemberClick = () => {
    const slug = activeEliteMember?.slug;
    if (!slug) return;
    window.open(`${ARTIST_PORTAL_BASE}/${slug}`, '_blank', 'noopener,noreferrer');
  };

  // Derive a simple UI context for the assistant based on the current route
  const assistantUiContext: AssistantUiContext = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/profile')) {
      return {
        page: 'profile',
        sectionId: path.replace('/profile/', '') || 'overview',
      };
    }
    if (path.startsWith('/jobs')) {
      return { page: 'jobs' };
    }
    if (path === '/wallet') {
      return { page: 'wallet' };
    }
    if (path === '/membership' || path === '/compare-plans') {
      return { page: 'membership' };
    }
    if (path === '/statistics') {
      return { page: 'statistics' };
    }
    if (path === '/settings') {
      return { page: 'settings' };
    }
    if (path === '/contact-us') {
      return { page: 'contact-us' };
    }
    if (path === '/faq') {
      return { page: 'faq' };
    }
    if (path === '/assistant') {
      return { page: 'assistant' };
    }
    return { page: 'home' };
  }, [location.pathname]);

  // Plan badge color (sidebar / membership) from profile review state
  const normalizedProfileStatus = profileStatus.toLowerCase();
  const planBadgeStatusClass =
    normalizedProfileStatus === 'approved' || normalizedProfileStatus === 'live'
      ? 'text-emerald-400'
      : normalizedProfileStatus === 'pending' || normalizedProfileStatus === 'on review'
        ? 'text-amber-500'
        : 'text-amber-500'; // Incomplete or unknown -> amber

  // Fetch details on mount (validates session; on 401 redirects to login)
  useEffect(() => {
    fetchArtistDetails();
  }, [fetchArtistDetails]);

  // Fetch Elite Members list (rotating display on the dashboard card)
  useEffect(() => {
    let cancelled = false;

    const loadEliteMembers = async () => {
      try {
        const res = await apiClient.get<EliteMembersResponse>(API_ENDPOINTS.AUTH.ELITE_MEMBERS);
        if (cancelled) return;

        // apiClient.get() returns AxiosResponse<T>, so the payload is under `res.data`
        const payload = res?.data;
        const members = payload?.success && Array.isArray(payload.data) ? payload.data : [];
        setEliteMembers(members);
        setEliteMemberIndex(0);
        setEliteIsFading(false);
      } catch (e) {
        // Keep UI resilient; user asked for the display, but we don't want the page to break.
        // eslint-disable-next-line no-console
        console.error('Failed to load elite members', e);
      }
    };

    loadEliteMembers();
    return () => {
      cancelled = true;
    };
  }, []);

  // Rotate through elite members every ~3 seconds with a fade transition.
  useEffect(() => {
    if (eliteMembers.length <= 1) return;
    if (eliteIsHoverPaused) return;

    let timeoutId: number | undefined;

    const intervalId = window.setInterval(() => {
      setEliteIsFading(true);
      timeoutId = window.setTimeout(() => {
        setEliteMemberIndex((i) => (i + 1) % eliteMembers.length);
        setEliteIsFading(false);
      }, 450);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [eliteMembers.length, eliteIsHoverPaused]);

  // Fetch Category Performance graph data for the dashboard card
  useEffect(() => {
    let cancelled = false;

    const fetchCategoryPerformance = async () => {
      try {
        setCategoryPerformanceLoading(true);
        setCategoryPerformanceError(null);

        const response = await apiClient.get<CategoryPerformanceResponse>(
          API_ENDPOINTS.ANALYTICS.CATEGORY_PERFORMANCE,
          { params: { period: categoryPerformancePeriod } }
        );

        if (cancelled) return;

        const payload = response?.data;
        if (payload?.success && payload.data) {
          setCategoryPerformance(payload.data);
        } else {
          setCategoryPerformance(null);
          setCategoryPerformanceError('Could not load category performance.');
        }
      } catch {
        if (!cancelled) {
          setCategoryPerformance(null);
          setCategoryPerformanceError('Could not load category performance.');
        }
      } finally {
        if (!cancelled) setCategoryPerformanceLoading(false);
      }
    };

    fetchCategoryPerformance();
    return () => {
      cancelled = true;
    };
  }, [categoryPerformancePeriod]);

  // Fetch Regional Rank data for podium + list on dashboard card
  useEffect(() => {
    let cancelled = false;

    const fetchRegionalRank = async () => {
      try {
        const response = await apiClient.get<RegionalRankResponse>(
          API_ENDPOINTS.ANALYTICS.REGIONAL_RANK
        );
        if (cancelled) return;

        const payload = response?.data;
        if (payload?.success && payload.data) {
          setRegionalRankData(payload.data);
        } else {
          setRegionalRankData(null);
        }
      } catch {
        if (!cancelled) setRegionalRankData(null);
      }
    };

    fetchRegionalRank();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch personal details once so location can be shown on profile overview
  useEffect(() => {
    if (!location.pathname.startsWith('/profile')) return;
    ensurePersonalDetailsFetched();
  }, [ensurePersonalDetailsFetched, location.pathname]);

  // Auto-show membership nudge sheet on user-home (artist-specific, every 2 days)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!artistDetails) return;

    const path = location.pathname;
    const isOnUserHome = path === '/user-home' || path === '/home';
    if (!isOnUserHome) return;
    if (sessionStorage.getItem(STORAGE_KEYS.LOGIN_TO_USER_HOME) !== '1') return;
    sessionStorage.removeItem(STORAGE_KEYS.LOGIN_TO_USER_HOME);

    const status = String(userInfo?.profile_status ?? '').trim().toLowerCase();
    const subscriptionName = String(subscriptions?.subscription_name ?? '').trim().toLowerCase();

    const isFree = subscriptionName === 'free';
    const isEconomy = subscriptionName === 'economy';
    const isOnReview = status === 'on review' || status === 'on_review' || status === 'pending';
    const isIncomplete = status === 'incomplete';
    const isLiveStatus = status === 'live' || status === 'approved';

    let variant: typeof membershipNudgeVariant | null = null;
    if (isLiveStatus && isFree) variant = 'live_free';
    else if (isIncomplete && (isFree || isEconomy)) variant = 'incomplete_free';
    else if (isOnReview && isFree) variant = 'on_review';

    if (!variant) return;

    const artistKey = artistSlug || String(userInfo?.email || user?.email || user?.id || 'unknown');
    const shouldShow = async () => {
      const lastShown = await storageManager.get<number>(
        STORAGE_KEYS.MEMBERSHIP_NUDGE_LAST_SHOWN(artistKey)
      );
      const now = Date.now();
      const twoDaysMs = 2 * 24 * 60 * 60 * 1000;
      if (lastShown != null && now - lastShown < twoDaysMs) return;

      setMembershipNudgeVariant(variant);
      setIsMembershipNudgeOpen(true);
      await storageManager.set(STORAGE_KEYS.MEMBERSHIP_NUDGE_LAST_SHOWN(artistKey), now);
    };

    void shouldShow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    artistDetails,
    location.pathname,
    artistSlug,
    userInfo?.profile_status,
    subscriptions?.subscription_name,
  ]);

  // Fetch booking forms on home so we can show "forms to fill" card when applicable
  useEffect(() => {
    ensureBookingFormsFetched();
  }, [ensureBookingFormsFetched]);

  // First-time user: show confetti once after onboarding redirect
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const raw = await storageManager.get<boolean>(STORAGE_KEYS.FIRST_TIME_USER_HOME);
      if (cancelled || !raw) return;
      setIsFirstTimeWelcome(true);
      await storageManager.remove(STORAGE_KEYS.FIRST_TIME_USER_HOME);
      if (cancelled) return;
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
      const t = setTimeout(() => {
        confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
      }, 200);
      return () => clearTimeout(t);
    };
    run();
    return () => { cancelled = true; };
  }, []);

  // Home tab mock data - replace with API
  const levelData = { currentLevel: 'Rising Star', nextLevel: 'Performer', xp: 320, xpNeeded: 500 };
  const profileStrength = useMemo(
    () => (isProfileCompletionFullyComplete(profileCompletion) ? 100 : fullProfilePercent),
    [profileCompletion, fullProfilePercent]
  );
  const homePriorityTasks = useMemo(
    () => getIncompletePriorityTasks(profileCompletion, 3),
    [profileCompletion]
  );
  const topJobs: ArtistDetailsJob[] = (artistDetails?.jobs ?? []).slice(0, 4);
  const streakData = { activeStreakDays: 4, appliedThisWeek: 3, weeklyGoal: 5, consistencyPercent: 86, activeDays: 24, bestStreak: 7, weeklyActivityScore: 68 };

  const [activeRankTab, setActiveRankTab] = useState<'regional' | 'air'>('regional');
  const [credStep, setCredStep] = useState(1);
  const [credYear, setCredYear] = useState<string | null>(null);
  const [credCityMode, setCredCityMode] = useState<'select' | 'custom'>('select');
  const [credCity, setCredCity] = useState<string | null>(null);
  const [credEventType, setCredEventType] = useState<string | null>(null);
  const [credEventTypeDisplay, setCredEventTypeDisplay] = useState<string | null>(null);
  const [credClient, setCredClient] = useState('');
  const [credSubmitted, setCredSubmitted] = useState(false);
  const [isCredibilityLoading, setIsCredibilityLoading] = useState(true);
  const [isCredibilitySubmitting, setIsCredibilitySubmitting] = useState(false);
  const [activeBanner, setActiveBanner] = useState(0);
  const [isBannerHovered, setIsBannerHovered] = useState(false);

  const credibilityCities = PERFORMANCE_CITIES;

  const normalizeNullableString = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim() ?? '';
    return trimmed ? trimmed : null;
  };

  type ArtistCredibilityResponse = {
    success: boolean;
    data: {
      first_gig: number | null;
      where: string | null;
      // Backend may return either a slug string OR an object like { slug, name }.
      event_type: string | { slug: string; name: string } | null;
      performed_for: string | null;
    };
  };

  const parseEventType = (
    eventType: ArtistCredibilityResponse['data']['event_type']
  ): { slug: string | null; name: string | null } => {
    if (typeof eventType === 'string') {
      return { slug: normalizeNullableString(eventType), name: null };
    }
    if (eventType && typeof eventType === 'object') {
      // Backend may respond with { slug, name }.
      return {
        slug: normalizeNullableString((eventType as { slug?: string }).slug),
        name: normalizeNullableString((eventType as { name?: string }).name),
      };
    }
    return { slug: null, name: null };
  };

  const applyCredibilityFromApi = (next: ArtistCredibilityResponse['data']) => {
    const nextWhere = normalizeNullableString(next.where);
    const nextPerformedFor = normalizeNullableString(next.performed_for);
    const { slug: nextEventTypeSlug, name: nextEventTypeName } = parseEventType(next.event_type);

    const nextEventTypeDisplay =
      nextEventTypeName ??
      (nextEventTypeSlug
        ? EVENT_TYPES.find((t) => t.e_slug === nextEventTypeSlug)?.event_name ?? nextEventTypeSlug
        : null);

    const hasAnyValue =
      next.first_gig !== null ||
      nextWhere !== null ||
      nextEventTypeSlug !== null ||
      nextEventTypeName !== null ||
      nextPerformedFor !== null;

    setCredYear(next.first_gig !== null ? String(next.first_gig) : null);
    setCredCity(nextWhere);
    setCredEventType(nextEventTypeSlug);
    setCredEventTypeDisplay(nextEventTypeDisplay);
    setCredClient(nextPerformedFor ?? '');
    setCredCityMode(nextWhere ? (credibilityCities.includes(nextWhere) ? 'select' : 'custom') : 'select');

    if (hasAnyValue) {
      setCredSubmitted(true);
      setCredStep(4);
    } else {
      setCredSubmitted(false);
      setCredStep(1);
    }
  };

  const handleCredibilitySubmit = async () => {
    if (isCredibilitySubmitting) return;

    const whereValue = credCity?.trim();
    const performedForValue = credClient.trim();
    const firstGigValue = credYear ? Number(credYear) : NaN;
    const eventTypeValue = credEventType ?? null;

    if (!Number.isFinite(firstGigValue)) {
      toast.error('Please select a valid year.');
      return;
    }
    if (!whereValue) {
      toast.error('Please select or enter a location.');
      return;
    }
    if (!eventTypeValue) {
      toast.error('Please select an event type.');
      return;
    }
    if (!performedForValue) {
      toast.error('Please enter the client / brand name.');
      return;
    }

    setIsCredibilitySubmitting(true);
    try {
      await apiClient.put(API_ENDPOINTS.AUTH.ARTIST_CREDIBILITY, {
        first_gig: firstGigValue,
        where: whereValue,
        event_type: eventTypeValue,
        performed_for: performedForValue,
      });

      // Refresh from GET after PUT (backend may normalize values).
      const res = await apiClient.get<ArtistCredibilityResponse>(
        API_ENDPOINTS.AUTH.ARTIST_CREDIBILITY
      );

      if (!res.data?.success || !res.data.data) {
        throw new Error('Failed to fetch updated credibility.');
      }

      const next = res.data.data;

      applyCredibilityFromApi(next);
    } catch {
      toast.error('Failed to save credibility. Please try again.');
    } finally {
      setIsCredibilitySubmitting(false);
    }
  };

  const androidAppUrl = import.meta.env.VITE_ANDROID_APP_URL;
  const iosAppUrl = import.meta.env.VITE_IOS_APP_URL;
  const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.cockpit.app';

  const handleDownloadAndroid = useCallback(() => {
    window.open(googlePlayUrl, '_blank', 'noopener,noreferrer');
  }, [googlePlayUrl]);

  const handleDownloadIOS = useCallback(() => {
    toast('iOS app coming soon.', { icon: 'ℹ️' });
  }, []);

  const handleDownloadCardClick = useCallback(() => {
    const url = androidAppUrl || iosAppUrl;
    if (!url) {
      toast.error('App store links are not configured.');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [androidAppUrl, iosAppUrl]);

  const bannerSlides = useMemo(
    () => [
      ...(showExpressBookingFeatures ? [{ id: 'express', type: 'express' as const }] : []),
      ...(!isNativeApp ? [{ id: 'download-apps', type: 'downloadApps' as const }] : []),
      ...(profileStrength <= 99
        ? [{ id: 'profile-reward', type: 'profileReward' as const }]
        : []),
      { id: 'vip-upgrade', type: 'vipUpgrade' as const },
    ],
    [profileStrength, isNativeApp, showExpressBookingFeatures]
  );

  useEffect(() => {
    if (activeBanner >= bannerSlides.length) {
      setActiveBanner(0);
    }
  }, [activeBanner, bannerSlides.length]);

  useEffect(() => {
    if (isBannerHovered) return;

    const interval = setInterval(() => {
      setActiveBanner((prev) => (prev + 1) % bannerSlides.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [isBannerHovered, bannerSlides.length]);

  // Credibility: always load existing data first, then decide whether
  // to show the summary or the step-1 form.
  useEffect(() => {
    if (!showExpressBookingFeatures) {
      setIsCredibilityLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsCredibilityLoading(true);
      try {
        const res = await apiClient.get<ArtistCredibilityResponse>(
          API_ENDPOINTS.AUTH.ARTIST_CREDIBILITY
        );

        if (cancelled) return;

        if (!res.data?.success || !res.data.data) {
          // If API returns success but no data, treat as empty credibility.
          applyCredibilityFromApi({
            first_gig: null,
            where: null,
            event_type: null,
            performed_for: null,
          });
          return;
        }

        applyCredibilityFromApi(res.data.data);
      } catch {
        if (cancelled) return;
        applyCredibilityFromApi({
          first_gig: null,
          where: null,
          event_type: null,
          performed_for: null,
        });
      } finally {
        if (cancelled) return;
        setIsCredibilityLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [showExpressBookingFeatures]);

  useEffect(() => {
    if (
      !showExpressBookingFeatures &&
      (location.pathname === '/express-booking' ||
        location.pathname === '/express-booking-consent-form')
    ) {
      navigate('/user-home', { replace: true });
    }
  }, [location.pathname, navigate, showExpressBookingFeatures]);

  const firstName = userName.split(' ')[0];
  const greetingHeading = isFirstTimeWelcome
    ? `Welcome to your dashboard, ${firstName}`
    : `Welcome back, ${firstName}`;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    sessionStorage.setItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST, '1');
    try {
      useArtistDetailsStore.getState().clearArtistDetails();
      const success = await AuthService.logout();
      if (success) {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_SUCCESS_MESSAGE, 'Logged out successfully.');
        navigate('/login', { replace: true });
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
        toast.error('Logout failed. Please try again.');
      }
    } catch (error) {
      sessionStorage.removeItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST);
      throw error;
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Sync activeNav with URL
  useEffect(() => {
    const path = location.pathname;
    if (path === '/user-home' || path === '/home') {
      setActiveNav('home');
    } else {
      // Map routes to nav IDs
      const routeMap: Record<string, string> = {
        '/assistant': 'assistant',
        '/profile': 'profile',
        '/profile/gallery': 'profile',
        '/profile/personal-details': 'profile',
        '/profile/performance-details': 'profile',
        '/profile/usp': 'profile',
        '/profile/story': 'profile',
        '/profile/awards-achievements': 'profile',
        '/profile/past-performances': 'profile',
        '/profile/social-media': 'profile',
        '/profile/event-essentials': 'profile',
        '/jobs': 'jobs',
        '/wallet': 'wallet',
        '/membership': 'membership',
        '/compare-plans': 'membership',
        '/express-booking': 'express-booking',
        '/express-booking-consent-form': 'express-booking',
        '/statistics': 'statistics',
        '/settings': 'settings',
        '/contact-us': 'contact-us',
        '/faq': 'faq',
      };
      setActiveNav(path.startsWith('/jobs') ? 'jobs' : routeMap[path] || 'home');
    }
  }, [location.pathname]);

  const handleNavClick = (item: NavItem) => {
    // Don't navigate for items with popovers (contact, utilities, website)
    if (item.hasChevron) {
      return;
    }
    // Navigate to the specific page
    if (item.id === 'home') {
      navigate('/user-home');
    } else {
      navigate(`/${item.path}`);
    }
  };

  const handleUtilityClick = (utilityPath: string) => {
    // Navigate to the specific utility page
    navigate(`/${utilityPath}`);
  };

  const handleWebsiteDesignClick = (plan: string) => {
    const planKey = String(plan ?? '').toLowerCase();
    const validPlan = ['free', 'economy', 'premium', 'elite'].includes(planKey) ? planKey : 'free';
    navigate(`/preview-my-website?plan=${validPlan}`);
  };

  // Primary contact details from artist details (fallback to defaults)
  const contactEmail = userInfo?.email || 'Not provided';
  const contactPhone = userInfo?.phone || 'Not provided';

  // Update popover position when item is hovered (keeps popover within viewport vertically)
  useEffect(() => {
    if (hoveredItem && navItemRefs.current[hoveredItem]) {
      const element = navItemRefs.current[hoveredItem];
      if (element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight =
          typeof window !== 'undefined'
            ? window.innerHeight || document.documentElement.clientHeight
            : 0;

        // Rough height estimates per popover type so we can clamp to viewport
        let estimatedHeight = 180;
        if (hoveredItem === 'utilities' || hoveredItem === 'website') {
          estimatedHeight = 160;
        } else if (hoveredItem === 'contact') {
          estimatedHeight = 140;
        }

        const verticalMargin = 16; // margin from top/bottom of viewport
        let top = rect.top;

        if (viewportHeight) {
          // If popover would overflow bottom, shift it up
          if (top + estimatedHeight > viewportHeight - verticalMargin) {
            top = Math.max(verticalMargin, viewportHeight - estimatedHeight - verticalMargin);
          }
          // Additional safety to avoid going above the viewport
          if (top < verticalMargin) {
            top = verticalMargin;
          }
        }

        setPopoverPosition({
          top,
          left: rect.right + 8, // 8px gap (ml-2 equivalent)
        });
      }
    } else {
      setPopoverPosition(null);
    }
  }, [hoveredItem]);

  const updateProfileCompletionPanelPosition = useCallback(() => {
    const el = profileCompletionBarRef.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    const panelWidth = 280;
    const left = Math.max(12, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 12));
    setProfileCompletionPanelPos({ top: rect.bottom + 10, left });
  }, []);

  useEffect(() => {
    if (!profileCompletionHover) {
      setProfileCompletionPanelPos(null);
      return;
    }
    updateProfileCompletionPanelPosition();
    const onReposition = () => updateProfileCompletionPanelPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [profileCompletionHover, updateProfileCompletionPanelPosition]);

  const openProfileCompletionDesktop = useCallback(() => {
    if (profileCompletionLeaveTimerRef.current) {
      window.clearTimeout(profileCompletionLeaveTimerRef.current);
      profileCompletionLeaveTimerRef.current = null;
    }
    setProfileCompletionHover(true);
  }, []);

  const scheduleCloseProfileCompletionDesktop = useCallback(() => {
    if (profileCompletionLeaveTimerRef.current) {
      window.clearTimeout(profileCompletionLeaveTimerRef.current);
    }
    profileCompletionLeaveTimerRef.current = window.setTimeout(() => {
      setProfileCompletionHover(false);
      profileCompletionLeaveTimerRef.current = null;
    }, 120);
  }, []);

  useEffect(() => {
    return () => {
      if (profileCompletionLeaveTimerRef.current) {
        window.clearTimeout(profileCompletionLeaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!profileCompletionMobileOpen && !profileCompletionHover) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setProfileCompletionMobileOpen(false);
        setProfileCompletionHover(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [profileCompletionMobileOpen, profileCompletionHover]);

  useEffect(() => {
    if (!profileCompletionMobileOpen) return;
    const onDown = (e: MouseEvent) => {
      const bar = profileCompletionBarRef.current;
      if (bar && !bar.contains(e.target as Node)) {
        setProfileCompletionMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [profileCompletionMobileOpen]);

  // Scroll page content to top when route changes (for profile subpages etc.)
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  // Major navigation items (grouped together)
  const majorNavItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [
      { id: 'home', label: 'Home', icon: <Home className="h-5 w-5" />, path: 'home' },
      // { id: 'assistant', label: 'AI Assistant', icon: <Sparkles className="h-5 w-5" />, path: 'assistant' },
      { id: 'profile', label: 'Profile', icon: <User className="h-5 w-5" />, path: 'profile' },
      { id: 'jobs', label: 'Jobs', icon: <FolderPlus className="h-5 w-5" />, path: 'jobs' },
      {
        id: 'express-booking',
        label: 'Express Booking',
        icon: <Rocket className="h-5 w-5" />,
        path: expressBookingNavPath,
      },
      { id: 'wallet', label: 'Wallet', icon: <Wallet className="h-5 w-5" />, path: 'wallet' },
      { id: 'membership', label: 'Level Up', icon: <Star className="h-5 w-5" />, path: 'membership' },
      { id: 'statistics', label: 'Statistics', icon: <TrendingUp className="h-5 w-5" />, path: 'statistics' },
    ];

    return showExpressBookingFeatures
      ? items
      : items.filter((item) => item.id !== 'express-booking');
  }, [expressBookingNavPath, showExpressBookingFeatures]);

  // Other navigation items
  const otherNavItems: NavItem[] = [
    { id: 'contact', label: 'Contact Details', icon: <Smartphone className="h-5 w-5" />, path: 'contact', hasChevron: true },
    { id: 'utilities', label: 'Utilities', icon: <BarChart3 className="h-5 w-5" />, path: 'utilities', hasChevron: true },
    { id: 'website', label: 'Preview My Website', icon: <Sparkles className="h-5 w-5" />, path: 'website', hasChevron: true },
  ];

  // Mobile-only nav: Home, Profile, Statistics, Contact Details (black box)
  const mobileMajorNavItems: NavItem[] = [
    { id: 'home', label: 'Home', icon: <Home className="h-5 w-5" />, path: 'home' },
    // { id: 'assistant', label: 'AI Assistant', icon: <Sparkles className="h-5 w-5" />, path: 'assistant' },
    { id: 'profile', label: 'Profile', icon: <User className="h-5 w-5" />, path: 'profile' },
    { id: 'statistics', label: 'Statistics', icon: <TrendingUp className="h-5 w-5" />, path: 'statistics' },
    { id: 'contact', label: 'Contact Details', icon: <Smartphone className="h-5 w-5" />, path: 'profile/event-essentials' },
  ];

  const categoryPerformanceChartData =
    categoryPerformance?.monthly_bookings?.map((item) => ({
      label: item.label,
      value: item.booking_count,
    })) ?? [];

  const categoryPerformanceTotalBookings = categoryPerformance?.total_bookings ?? null;

  return (
    <>
      <div
        className="bg-[#0B0B0D] text-white flex flex-col overflow-hidden"
        style={{
          height: isKeyboardOpen ? 'var(--visual-viewport-height, 100dvh)' : '100vh',
        }}
      >
        {isLoggingOut && <LoggingOutOverlay />}
        {/* Top Navbar - Full Width */}
        <header
          className="
    fixed top-0 z-[70]
    h-16 w-full
    flex items-center justify-between px-6
    bg-transparent
    backdrop-blur-md
    supports-[backdrop-filter]:bg-transparent
    after:absolute after:inset-x-0 after:bottom-0 after:h-6
    after:pointer-events-none
  "
          style={{
            WebkitMaskImage:
              'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
            maskImage:
              'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
          }}
        >
          {/* Left Side - Hamburger (Mobile) or Back Button (Desktop) */}
          <div className="flex items-center gap-4">
            {/* Hamburger Menu - Mobile Only */}
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5 text-white" />
            </button>

            {/* Back Button - Desktop Only */}
            <div className="hidden lg:block">
              <BackButton fallbackRoute="/user-home" />
            </div>
          </div>

          {/* Center - Logo Placeholder */}
          <div className="flex-1 flex justify-center lg:justify-start lg:ml-4">
            {/* Logo will be placed here */}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-4">
            {activeExpressEvent && (
              <div className="hidden lg:block">
                <button
                  type="button"
                  onClick={() =>
                    navigate('/jobs', {
                      state: { tab: 'express', spotlightBookingDealId: activeExpressEvent.dealId },
                    })
                  }
                  className="rounded-full border border-[#F33363]/40 bg-[#F33363]/15 px-4 py-2 text-xs font-bold tracking-wide text-[#FFD3E0] transition-colors hover:bg-[#F33363]/25"
                >
                  Event Ongoing: {ongoingEventCountdown}
                </button>
              </div>
            )}
            {/* Back to Agency - Desktop navbar only (hidden on mobile; use sheet option instead) */}
            {isAgencyUser && (
              <div className="hidden lg:block">
                <button
                  type="button"
                  onClick={() => navigate('/agency-home')}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#2C2C2E] transition-colors text-white"
                  aria-label="Back to Agency"
                >
                  <Building2 className="h-5 w-5 text-[#8E8E93]" />
                  <span className="text-sm font-medium">Back to Agency</span>
                </button>
              </div>
            )}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors"
              aria-label="Search"
            >
              <Search className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setIsNotificationsOpen(true)}
              className="p-2 rounded-lg hover:bg-[#2C2C2E] transition-colors relative"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5 text-white" />
              {/* TODO: Add notification badge if unread count > 0 */}
            </button>
            {/* Profile Icon - Mobile Only */}
            <button
              onClick={() => navigate('/profile')}
              className="lg:hidden p-1.5 rounded-full hover:bg-[#2C2C2E] transition-colors"
              aria-label="Profile"
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-[#2C2C2E] flex items-center justify-center">
                {profilePic ? (
                  <img
                    src={profilePic}
                    alt={userName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-4 w-4 text-[#E5E5EA]" />
                )}
              </div>
            </button>
          </div>
        </header>

        {/* Main Content Area - Sidebar + Content */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Sidebar - Hidden on Mobile */}
          <div className="hidden lg:block">
            <aside className="flex w-72 bg-[#1C1C1E]/60  flex-col shadow-2xl h-full overflow-hidden">
              {/* Profile Section */}
              <div className="px-8 pt-20 relative">
                {/* Live Indicator - Refined */}
                {isLive && (
                  <div className="absolute top-16 right-6 flex items-center gap-1.5 px-2 py-1 bg-[#F33363]/10 rounded-full border border-[#F33363]/20">
                    <div className="relative">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F33363]" />
                      <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-[#F33363] animate-ping opacity-75" />
                    </div>
                    <span className="text-[9px] text-[#F33363] font-black uppercase tracking-wider">Live</span>
                  </div>
                )}

                <div className="flex flex-col items-center">
                  {/* Profile Avatar with circular % progress and label + bar below */}
                  <div className="relative mb-6">
                    <ProfilePicWithProgress
                      src={profilePic}
                      alt={userName}
                      percent={fullProfilePercent}
                      size="md"
                      showLabelAndBar
                    />
                  </div>

                  <div className="text-center space-y-1">
                    <h2 className="text-xl font-black text-white tracking-tight">{userName}</h2>
                    {artistCategoryName ? (
                      <p className="text-xs text-[#8E8E93] font-medium uppercase tracking-wide">{artistCategoryName}</p>
                    ) : null}
                    <div className="inline-flex justify-center items-center gap-1.5 px-3 py-1 rounded-full">
                      <span className="text-[10px] font-black text-[#E5E5EA] uppercase tracking-widest leading-tight text-center">
                        <span className="block">Plan: {planDisplayName}</span>
                        <span className="block">
                          Status:{' '}
                          <span className={planBadgeStatusClass}>
                            {profileStatus}
                          </span>
                        </span>
                        {subscriptionExpireLabel ? (
                          <span className="block">
                            Expire :{' '}
                            <span className="text-white">{subscriptionExpireLabel}</span>
                          </span>
                        ) : null}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Sophisticated Divider */}
                <div className="mt-8 flex justify-center">
                  <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#3A3A3C] to-transparent" />
                </div>
              </div>

              {/* Main Navigation */}
              <nav className="flex-1 overflow-y-auto w-full flex flex-col min-h-0">
                {/* Preview My Website - Above Major Nav */}
                <div className="w-full px-4 space-y-1 mb-2">
                  {otherNavItems
                    .filter(item => item.id === 'website')
                    .map((item) => (
                      <div
                        key={item.id}
                        ref={(el) => {
                          navItemRefs.current[item.id] = el;
                        }}
                        className="relative"
                        onMouseEnter={() => setHoveredItem(item.id)}
                        onMouseLeave={() => setHoveredItem(null)}
                      >
                        <button
                          onClick={() => handleNavClick(item)}
                          className={cn(
                            'w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors text-white',
                            'hover:bg-[#2C2C2E]'
                          )}
                        >
                          <div className="flex items-center gap-3">
                            {item.icon}
                            <span className="text-sm font-medium">{item.label}</span>
                          </div>
                          <ChevronsRight className="h-4 w-4 text-white/40" />
                        </button>
                      </div>
                    ))}
                </div>

                {/* Major Navigation Items - Darker Background */}
                <div className="w-full px-2 py-2 bg-[#0B0B0D] rounded-l-2xl mx-0 my-2 space-y-1">
                  {majorNavItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleNavClick(item)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-1.5 rounded-lg transition-colors duration-200 ease-out text-white/40 font-normal',
                        'hover:bg-[#2C2C2E]',
                        activeNav === item.id && 'bg-[#2C2C2E] border-r-2 border-[#F33363] text-white font-bold'
                      )}
                    >
                      {item.icon}
                      <span className="text-sm transition-colors duration-200 ease-out">
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Other Navigation Items */}
                <div className="w-full px-4 space-y-1">
                  {otherNavItems
                    .filter(item => item.id !== 'website')
                    .map((item) => {
                      // Items with popovers (contact, utilities) - desktop hover only
                      if (item.hasChevron) {
                        const isHovered = hoveredItem === item.id;
                        return (
                          <div
                            key={item.id}
                            ref={(el) => {
                              navItemRefs.current[item.id] = el;
                            }}
                            className="relative"
                            onMouseEnter={() => setHoveredItem(item.id)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            <button
                              className={cn(
                                'w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors duration-200 ease-out text-white/40',
                                'hover:bg-[#2C2C2E]',
                                isHovered && 'text-white'
                              )}
                            >
                              <div className="flex items-center gap-3">
                                {item.icon}
                                <span className="text-sm font-medium transition-colors duration-200 ease-out">
                                  {item.label}
                                </span>
                              </div>
                              <ChevronsRight
                                className={cn(
                                  'h-4 w-4 text-white/40 transition-colors duration-200 ease-out',
                                  isHovered && 'text-white'
                                )}
                              />
                            </button>
                          </div>
                        );
                      }

                      // Regular navigation items (shouldn't happen for otherNavItems, but keeping for safety)
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavClick(item)}
                          className={cn(
                            'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-white',
                            'hover:bg-[#2C2C2E]',
                            activeNav === item.id && 'bg-[#2C2C2E] border-r-2 border-[#F33363]'
                          )}
                        >
                          {item.icon}
                          <span className="text-sm font-medium">{item.label}</span>
                        </button>
                      );
                    })}
                </div>
              </nav>

              {/* Hover Popover Portal - Desktop Only */}
              {hoveredItem && popoverPosition && typeof document !== 'undefined' ? (
                createPortal(
                  <div
                    className="hidden lg:block fixed z-50"
                    style={{
                      top: `${popoverPosition.top}px`,
                      left: `${popoverPosition.left}px`, // Use position directly (already has 8px gap)
                    }}
                    onMouseEnter={() => setHoveredItem(hoveredItem)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {/* Invisible bridge to fill the gap - positioned to cover the 8px gap */}
                    <div
                      className="absolute -left-8 top-0 w-8 h-full"
                      onMouseEnter={() => setHoveredItem(hoveredItem)}
                    />
                    {/* Popover content - Desktop: contact details with single Edit → navigate to Event Essentials Contact Details tab (no sheet) */}
                    <div className="min-w-[200px] rounded-lg p-3 shadow-lg border border-[#3A3A3C]/50 bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
                      {hoveredItem === 'contact' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-[#8E8E93] uppercase tracking-wider">Contact Details</span>
                            <button
                              onClick={() => {
                                navigate('/profile/event-essentials?tab=contact-details');
                                setHoveredItem(null);
                              }}
                              className="p-1 rounded hover:bg-[#2C2C2E] transition-colors"
                              aria-label="Edit contact details"
                            >
                              <Edit className="h-3.5 w-3.5 text-[#8E8E93] hover:text-white" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-white">
                            {/* <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" /> */}
                            <Mail className="h-4 w-4 text-[#8E8E93] flex-shrink-0" />
                            <span>{contactEmail}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-white">
                            {/* <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" /> */}
                            <Phone className="h-4 w-4 text-[#8E8E93] flex-shrink-0" />
                            <span>{contactPhone}</span>
                          </div>
                        </div>
                      )}
                      {hoveredItem === 'utilities' && (
                        <div className="space-y-1">
                          <button
                            onClick={() => handleUtilityClick('settings')}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <Settings className="h-4 w-4 text-[#8E8E93]" />
                            <span>Settings</span>
                          </button>
                          <button
                            onClick={() => handleUtilityClick('contact-us')}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <Mail className="h-4 w-4 text-[#8E8E93]" />
                            <span>Contact Us</span>
                          </button>
                          <button
                            onClick={() => handleUtilityClick('faq')}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <HelpCircle className="h-4 w-4 text-[#8E8E93]" />
                            <span>FAQ</span>
                          </button>
                        </div>
                      )}
                      {hoveredItem === 'website' && (
                        <div className="space-y-1">
                          <button
                            onClick={() => handleWebsiteDesignClick('free')}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <span>Free</span>
                            <ChevronRight className="h-3.5 w-3.5 text-[#8E8E93]" />
                          </button>
                          <button
                            onClick={() => handleWebsiteDesignClick('economy')}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <span>Economy</span>
                            <ChevronRight className="h-3.5 w-3.5 text-[#8E8E93]" />
                          </button>
                          <button
                            onClick={() => handleWebsiteDesignClick('premium')}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <span>Premium</span>
                            <ChevronRight className="h-3.5 w-3.5 text-[#8E8E93]" />
                          </button>
                          <button
                            onClick={() => handleWebsiteDesignClick('elite')}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-[#2C2C2E] text-sm text-white transition-colors"
                          >
                            <span>Elite</span>
                            <ChevronRight className="h-3.5 w-3.5 text-[#8E8E93]" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body
                )
              ) : null}

              {/* Logout Button */}
              <div className="p-4">
                <button
                  onClick={handleLogout}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white text-sm font-medium transition-colors"
                >
                  Logout
                </button>
              </div>
            </aside>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden w-full">
            {/* Page Content */}
            <main
              ref={mainRef}
              className="flex-1 overflow-y-auto overscroll-y-contain bg-[#0B0B0D] p-4 lg:pb-6 w-full content-safe-scroll"
              style={{
                ['--bottom-nav-reserved-height' as string]: isKeyboardOpen ? '0px' : '120px',
              }}
            >
              <div className="w-full">
                {showExpressBookingFeatures &&
                  location.pathname === '/express-booking-consent-form' && <ExpressBookingPage />}
                {showExpressBookingFeatures &&
                  location.pathname === '/express-booking' && <ExpressBookingEnablePage />}
                {activeNav === 'home' &&
                  (location.pathname === '/user-home' || location.pathname === '/home') && (
                    <div className="w-full md:px-4 py-10 pb-24">
                      {/* Premium Welcome Header */}
                      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 lg:mb-12 gap-6">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-2 h-2 rounded-full bg-[#F33363] animate-pulse" />
                            <span className="text-[10px] font-black text-[#8E8E93] uppercase tracking-[0.2em]">Dashboard Overview</span>
                          </div>
                          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tighter">
                            {greetingHeading.split(firstName)[0]}
                            <span className="text-[#F33363]">{firstName}!</span>
                            {greetingHeading.split(firstName)[1]}
                          </h1>
                          <p className="text-[#8E8E93] text-base lg:text-lg max-w-md font-medium">
                            You&apos;re on the{" "}
                            <span className="text-white border-b border-[#F33363]/30">
                              {planDisplayName}
                            </span>{" "}
                            Plan.

                            Profile status is{" "}
                            <span className="text-white border-b border-[#F33363]/30">
                              {profileStatus || "Incomplete"}
                            </span>
                            .
                          </p>
                        </div>
                        {/* <div className="hidden lg:flex items-center gap-4 p-4 rounded-3xl">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#F33363]/20 to-purple-500/20 flex items-center justify-center border border-white/5">
                            <Star className="h-6 w-6 text-[#F33363]" />
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-widest leading-none mb-1">Star Status</p>
                            <p className="text-sm font-bold text-white tracking-tight leading-none italic">Verified Artist</p>
                          </div>
                        </div> */}
                      </div>

                      <div className="space-y-8 ">
                        {/* Highlight Cards Grid: with booking forms → My Bookings first, then Growth; else → Growth first, then Profile completion */}
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
                          {bookingFormsToFill.length >= 1 ? (
                            <>
                              {/* 1. My Bookings card (green) – first when forms to fill */}
                              <motion.button
                                type="button"
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                onClick={() => navigate('/jobs', { state: { tab: 'express' } })}
                                className="relative overflow-hidden w-full text-left rounded-3xl border border-[#1f2933]/70 bg-[#020617] p-4 lg:p-5 shadow-xl group"
                              >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-sky-500/10 blur-3xl rounded-full -mr-10 -mt-10" />
                                <div className="relative z-10 flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-2xl bg-[#0b1120] flex items-center justify-center flex-shrink-0">
                                      <FileEdit className="h-4 w-4 text-sky-400" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-xs font-bold text-[#E5E5EA] uppercase tracking-[0.24em]">
                                        My Bookings
                                      </span>
                                      <span className="text-[11px] text-[#A1A1AA] line-clamp-1">
                                        {bookingFormsToFill.length > 0
                                          ? `You have ${bookingFormsToFill.length} booking form${bookingFormsToFill.length !== 1 ? 's' : ''} to complete.`
                                          : 'All booking forms are up to date.'}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    {bookingFormsToFill.length > 0 && (
                                      <div className="text-right">
                                        <div className="text-sm font-black text-white">
                                          {bookingFormsToFill.length}
                                        </div>
                                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-300">
                                          To Fill
                                        </div>
                                      </div>
                                    )}
                                    <span className="inline-flex items-center gap-2 py-1.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-[0.18em] bg-sky-500/15 text-sky-300 group-hover:bg-sky-500/25 group-hover:text-sky-100 transition-colors">
                                      {bookingFormsToFill.length > 0 ? 'Review Forms' : 'View Bookings'}
                                      <ChevronRight className="h-3.5 w-3.5" />
                                    </span>
                                  </div>
                                </div>
                              </motion.button>

                              {/* 2. Profile completion card – progress bar style */}
                              <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: 0.1 }}
                                className="relative overflow-hidden rounded-3xl  bg-[#111111] p-4 lg:p-5 shadow-xl"
                              >
                                {/* Progress fill background */}
                                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${profileStrength}%` }}
                                    transition={{ duration: 0.9, ease: 'easeOut' }}
                                    style={{ width: `${profileStrength}%` }}
                                    className="h-full bg-gradient-to-r from-[#F33363]/40 via-[#F33363]/30 to-transparent"
                                  />
                                </div>

                                {/* Content on top of bar */}
                                <div className="relative z-10 flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-2xl bg-[#1C1C1E] flex items-center justify-center flex-shrink-0">
                                      <TrendingUp className="h-4 w-4 text-[#F33363]" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-xs font-bold text-[#E5E5EA] uppercase tracking-[0.24em]">
                                        Profile Strength
                                      </span>
                                      <span className="text-[11px] text-[#A1A1AA] line-clamp-1">
                                        Complete your profile to unlock better matches and higher‑value gigs.
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-4 flex-shrink-0">
                                    <div className="text-right">
                                      <div className="text-sm font-black text-white">
                                        {profileStrength}%
                                      </div>
                                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9CA3AF]">
                                        Complete
                                      </div>
                                    </div>
                                    <Button
                                      onClick={() => navigate('/profile')}
                                      variant="active"
                                      className="py-1.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-[0.18em] shadow-md shadow-[#F33363]/25"
                                    >
                                      Proceed <ChevronRight className="h-4 w-4 " />
                                    </Button>
                                  </div>
                                </div>
                              </motion.div>

                            </>
                          ) : (
                            <>
                              {/* 2. Profile completion card – progress bar style */}
                              <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: 0.1 }}
                                className="relative overflow-hidden rounded-3xl  bg-[#111111] p-4 lg:p-5 shadow-xl"
                              >
                                {/* Progress fill background */}
                                <div className="absolute inset-0 overflow-hidden rounded-3xl">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${profileStrength}%` }}
                                    transition={{ duration: 0.9, ease: 'easeOut' }}
                                    style={{ width: `${profileStrength}%` }}
                                    className="h-full bg-gradient-to-r from-[#F33363]/40 via-[#F33363]/30 to-transparent"
                                  />
                                </div>

                                {/* Content on top of bar */}
                                <div className="relative z-10 flex items-center justify-between gap-4">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-2xl bg-[#1C1C1E] flex items-center justify-center flex-shrink-0">
                                      <TrendingUp className="h-4 w-4 text-[#F33363]" />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-xs font-bold text-[#E5E5EA] uppercase tracking-[0.24em]">
                                        Profile Strength
                                      </span>
                                      <span className="text-[11px] text-[#A1A1AA] line-clamp-1">
                                        Complete your profile to unlock better matches and higher‑value gigs.
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-4 flex-shrink-0">
                                    <div className="text-right">
                                      <div className="text-sm font-black text-white">
                                        {profileStrength}%
                                      </div>
                                      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9CA3AF]">
                                        Complete
                                      </div>
                                    </div>
                                    <Button
                                      onClick={() => navigate('/profile')}
                                      variant="active"
                                      className="py-1.5 px-3 rounded-xl text-[10px] font-black uppercase tracking-[0.18em] shadow-md shadow-[#F33363]/25"
                                    >
                                      Proceed <ChevronRight className="h-4 w-4 " />
                                    </Button>
                                  </div>
                                </div>
                              </motion.div>
                            </>
                          )}
                        </div>

                        {/* Express Booking Hero Banner + additional banners */}
                        <div
                          className="relative overflow-hidden rounded-[2.5rem] border-2 border-white/10 shadow-2xl bg-[#050506]"
                          onMouseEnter={() => setIsBannerHovered(true)}
                          onMouseLeave={() => setIsBannerHovered(false)}
                        >
                          <div
                            className="flex w-full transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeBanner * 100}%)` }}
                          >
                            {bannerSlides.map((slide) => (
                              <div key={slide.id} className="w-full flex-shrink-0">
                                {slide.type === 'express' ? (
                                  <div
                                    className="relative w-full h-full cursor-pointer"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate(expressBookingRoute)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate(expressBookingRoute);
                                      }
                                    }}
                                    style={{
                                      backgroundImage: `url(${expressCardBg})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                    }}
                                  >
                                    <div className="relative z-10 px-4 py-4 sm:px-6 sm:py-6 md:px-10 md:py-10 flex flex-col">
                                      {/* Top row: headline + logo */}
                                      <div className="flex p-3 w-full items-start gap-0 sm:gap-0 md:gap-6">
                                        <div className="flex-1 min-w-0">
                                          <ContainerTextFlip
                                            words={[
                                              'Get Booked the Same Day.',
                                              'Perform Without Delays.',
                                              'Accept First & Lock the Gig Instantly.',
                                            ]}
                                            interval={3600}
                                            animationDuration={1500}
                                            unstyled
                                            className="pt-0 pb-0 px-0 text-left"
                                            textClassName="text-left text-xl sm:text-2xl md:text-3xl lg:text-3xl font-black tracking-tight text-amber-200/90 drop-shadow-[0_0_18px_rgba(248,113,113,1)]"
                                          />
                                        </div>

                                        {/* Right: express logo / gif */}
                                        <div className="relative flex-shrink-0 w-32 h-16 sm:w-48 sm:h-20 md:w-60 md:h-20 lg:w-60 lg:h-20">
                                          <div className="absolute -top-6 -right-6 w-40 h-40 sm:w-40 sm:h-40 bg-[#f97316]/25 blur-3xl rounded-full pointer-events-none" />
                                          <div className="relative w-full h-full rounded-3xl overflow-hidden">
                                            <img
                                              src={EXPRESS_BOOKING_GIF_URL}
                                              alt="Express booking in action"
                                              className="w-full h-full object-contain"
                                            />
                                          </div>
                                        </div>
                                      </div>

                                      {/* Bottom row: feature pills */}
                                      <div className="mt-2 flex flex-wrap gap-2 sm:gap-2 justify-start md:justify-start md:items-start">
                                        {[
                                          { icon: Calendar, label: 'Same-day Booking' },
                                          { icon: CheckCircle2, label: 'Instant Lock' },
                                          { icon: Zap, label: 'Short-notice Ready' },
                                        ].map((item) => (
                                          <div
                                            key={item.label}
                                            className="flex items-center gap-1 rounded-xl bg-white/5 px-2 py-1 sm:px-2 sm:py-1 border border-white/10"
                                          >
                                            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl flex items-center justify-center bg-amber-500/10">
                                              <item.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-300 drop-shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                                            </div>
                                            <span className="text-[11px] sm:text-xs text-[#F5F5F7]">
                                              {item.label}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ) : slide.type === 'downloadApps' ? (
                                  !isNativeApp ? (
                                    <div
                                      className="relative w-full h-full rounded-[2.5rem] px-5 py-6 sm:px-8 sm:py-8 flex flex-col items-start justify-center gap-5 cursor-pointer"
                                      role="button"
                                      tabIndex={0}
                                      onClick={handleDownloadCardClick}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          handleDownloadCardClick();
                                        }
                                      }}
                                    >

                                      {/* Background: no image, but still looks like the other hero cards */}
                                      <div
                                        className="absolute inset-0 rounded-[2.5rem] pointer-events-none"
                                        style={{
                                          backgroundImage: `
                                          radial-gradient(circle at 10% 25%, rgba(56, 189, 248, 0.35), transparent 45%),
                                          radial-gradient(circle at 85% 60%, rgba(16, 185, 129, 0.28), transparent 50%),
                                          linear-gradient(90deg, #0B0B0D 0%, #151521 45%, #1B1030 100%)
                                        `,
                                        }}
                                      />
                                      <div
                                        className="absolute inset-0 rounded-[2.5rem] pointer-events-none opacity-30"
                                        style={{
                                          backgroundImage:
                                            'linear-gradient(rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.14) 1px, transparent 1px)',
                                          backgroundSize: '26px 26px',
                                          backgroundPosition: 'center',
                                        }}
                                      />
                                      <div className="absolute inset-0 rounded-[2.5rem] bg-black/25 pointer-events-none" />
                                      <div className="hidden md:flex justify-end">
                                        <p className="text-right text-xl sm:text-2xl md:text-3xl lg:text-3xl font-black tracking-tight text-amber-200/90 drop-shadow-[0_0_18px_rgba(248,113,113,0.8)]">
                                          Designed for your pocket.
                                        </p>
                                      </div>
                                      <div className="relative z-10 w-full flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                                        <div className="space-y-1.5">
                                          <p className="text-xs font-semibold tracking-[0.35em] text-[#FACC15]/70 uppercase">
                                            Download the app
                                          </p>
                                          <p className="text-lg sm:text-xl font-black text-white">
                                            Android + iOS
                                          </p>
                                        </div>



                                        <div className="flex flex-wrap gap-3">
                                          <button
                                            type="button"
                                            className="inline-flex items-center justify-center  bg-black/40 h-10 w-32 md:h-12 md:w-40 p-0 overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:bg-black/60 transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadAndroid();
                                            }}
                                          >
                                            <img
                                              src={downloadGooglePlayBadge}
                                              alt="Get it on Google Play"
                                              className="block w-full h-full object-cover"
                                            />
                                          </button>

                                          <button
                                            type="button"
                                            className="inline-flex items-center justify-center  bg-black/40 h-10 w-32 md:h-12 md:w-40 p-0 overflow-hidden shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:bg-black/60 transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownloadIOS();
                                            }}
                                          >
                                            <img
                                              src={downloadAppStoreBadge}
                                              alt="Download on the App Store"
                                              className="block w-full h-full object-cover"
                                            />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : null
                                ) : slide.type === 'profileReward' ? (
                                  <div
                                    className="relative w-full h-full rounded-[2.5rem] px-5 py-6 sm:px-8 sm:py-8 flex items-center justify-between gap-4 cursor-pointer"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate('/profile')}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate('/profile');
                                      }
                                    }}
                                    style={{
                                      background:
                                        'linear-gradient(90deg,rgb(46, 39, 34) 0%, #241E1C 10%, #050404 70%)',
                                    }}
                                  >
                                    <div className="flex flex-col min-w-0 space-y-3">
                                      <p className="text-xl sm:text-2xl md:text-3xl font-black tracking-tight text-white">
                                        Don’t leave your profile<br />half-baked!
                                      </p>
                                      <p className="text-sm sm:text-base text-[#E5E5EA] max-w-md">
                                        Complete karo, <span className="text-[#927F66] font-semibold">250 StarCoins</span> pao aur gigs pe apply karna shuru karo!
                                      </p>
                                    </div>
                                    <div className="hidden sm:flex flex-1 justify-end">
                                      <div className="relative w-28 h-24 md:w-52 md:h-28 ">
                                        <img
                                          src={starcoinAnimation}
                                          alt="Animated StarCoins"
                                          className="w-full h-full object-contain scale-[2]"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div
                                    className="relative w-full h-full bg-gradient-to-r from-[#0B0B0D] via-[#151521] to-[#1B1030] rounded-[2.5rem] px-5 py-6 sm:px-10 sm:py-8 flex flex-row items-center justify-between cursor-pointer"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => navigate('/membership')}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        navigate('/membership');
                                      }
                                    }}
                                  >
                                    <div className="flex flex-col min-w-0 space-y-0">
                                      <p className="text-xs font-semibold tracking-[0.35em] text-[#FACC15]/70 uppercase mb-3">
                                        Level Up
                                      </p>
                                      <div className="flex flex-col xl:flex-row items-start xl:items-center justify-center gap-0 xl:gap-2">
                                        <p className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight text-white">
                                          Soar above with
                                        </p>
                                        <p className="text-xl sm:text-2xl md:text-3xl font-medium text-amber-300">
                                          exclusive VIP Benefits
                                        </p>
                                      </div>
                                      <div className="mt-2">
                                        <ContainerTextFlip
                                          words={[
                                            'More gigs.',
                                            'Faster payouts.',
                                            'StarCoins perks.',
                                            'Priority support.',
                                          ]}
                                          interval={2600}
                                          animationDuration={1300}
                                          unstyled
                                          className="pt-0 px-0 text-center"
                                          textClassName="text-sm sm:text-base md:text-lg font-medium tracking-tight text-amber-400"
                                        />
                                      </div>
                                    </div>
                                    <div className="mt-4 sm:mt-0 flex flex-col items-center justify-center gap-4">
                                      <div className="relative w-12 h-12 md:w-20 md:h-20">
                                        <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle_at_top,_rgba(250,204,21,0.5),_transparent_60%)] opacity-60" />
                                        <img
                                          src={levelupCrown}
                                          alt="Level up crown"
                                          className="relative w-full h-full object-contain drop-shadow-[0_0_20px_rgba(250,204,21,0.9)]"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="inline-flex items-center px-6 py-2 rounded-full bg-black/40 border border-amber-300/60 text-sm font-semibold text-amber-200 shadow-[0_0_20px_rgba(0,0,0,0.8)] hover:bg-black/60 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate('/membership');
                                        }}
                                      >
                                        Upgrade
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          {/* Dots */}
                          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                            {bannerSlides.map((slide, index) => (
                              <button
                                key={slide.id}
                                type="button"
                                onClick={() => setActiveBanner(index)}
                                className={`h-1.5 rounded-full transition-all ${activeBanner === index ? 'w-4 bg-white' : 'w-1.5 bg-white/30'
                                  }`}
                                aria-label={`Go to banner ${index + 1}`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      {/* Middle Content Section */}
                      <div
                        className={cn(
                          'grid gap-8 xl:gap-8 mt-8',
                          showExpressBookingFeatures
                            ? 'grid-cols-1 md:grid-cols-2'
                            : 'grid-cols-1',
                        )}
                      >
                        {/* Left column: Priority Tasks (next 3 incomplete segments) or Hottest Opportunities */}
                        <div className="space-y-3">
                          {homePriorityTasks.length > 0 ? (
                            <>
                              <div className="flex items-center justify-between gap-4 mb-1">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#3F1018] via-[#1C1C1E] to-[#0B0B0D] flex items-center justify-center border border-[#F33363]/40 shadow-[0_0_18px_rgba(243,51,99,0.35)]">
                                    <Sparkles className="h-5 w-5 text-[#F33363]" />
                                  </div>
                                  <div className="flex flex-col">
                                    <h2 className="text-xs font-black text-[#F2F2F7] uppercase tracking-[0.32em]">
                                      Priority Tasks
                                    </h2>
                                    <p className="text-[11px] text-[#8E8E93]">
                                      Complete these to unlock your best gigs faster.
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {homePriorityTasks.map((task, index) => (
                                  <motion.button
                                    key={task.segmentId}
                                    type="button"
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.08 }}
                                    onClick={() => navigate(task.path)}
                                    className="w-full group flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#111111] via-[#141417] to-[#1C1C1E] px-4 py-3 border border-[#2F2F33] hover:border-[#F33363]/60 hover:from-[#151516] hover:via-[#19191D] hover:to-[#1F1F23] transition-all duration-300 text-left shadow-[0_0_24px_rgba(0,0,0,0.6)]"
                                  >
                                    <div className="flex flex-row items-center gap-3 min-w-0">
                                      <div className="w-9 h-9 rounded-xl bg-[#1F1F23] group-hover:bg-[#F33363]/15 flex items-center justify-center flex-shrink-0 transition-colors">
                                        {renderPriorityTaskIcon(task.segmentId)}
                                      </div>
                                      <div className="flex flex-col min-w-0">
                                        <p className="text-sm font-semibold text-[#F2F2F7] group-hover:text-white transition-colors truncate">
                                          {task.label}
                                        </p>
                                        <p className="text-[11px] text-[#8E8E93] mt-0.5 line-clamp-2">
                                          {task.description}
                                        </p>
                                      </div>
                                    </div>

                                    <ChevronRight className="h-4 w-4 text-[#4A4A4F] group-hover:text-[#F33363] transition-colors flex-shrink-0" />
                                  </motion.button>
                                ))}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-between gap-3 mb-1">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-9 h-9 rounded-2xl bg-[#111111] flex items-center justify-center border border-white/10 shadow-[0_8px_26px_rgba(0,0,0,0.8)]">
                                    <Flame className="h-4 w-4 text-[#F97316]" />
                                  </div>
                                  <div className="flex flex-col">
                                    <h2 className="text-[11px] font-semibold text-[#F5F5F7] tracking-[0.24em] uppercase">
                                      Hottest Opportunities
                                    </h2>
                                    <p className="text-[11px] text-[#8E8E93]">
                                      Curated gigs matched to your profile.
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => navigate('/jobs')}
                                  className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-medium text-[#F33363] uppercase tracking-[0.18em] hover:bg-white/8"
                                >
                                  View All
                                </button>
                              </div>

                              <div className="space-y-2">
                                {topJobs.map((job, index) => (
                                  <motion.article
                                    key={job.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + index * 0.05 }}
                                    onClick={() => navigate('/jobs')}
                                    className="group cursor-pointer flex flex-row justify-between sm:flex-row sm:items-center gap-2 sm:gap-4 bg-none hover:bg-[#2C2C2E]/40 border-[#2C2C2E]/40 rounded-2xl p-4 transition-all duration-300 border"
                                  >
                                    <div className="flex flex-row justify-between items-start md:items-center md:gap-4 gap-2 min-w-0 w-full">
                                      <div className="flex flex-col min-w-0">
                                        <p className="text-sm font-semibold text-white group-hover:text-white transition-colors line-clamp-1">
                                          {job.event_type} Event
                                        </p>
                                        <p className="text-xs text-[#8E8E93] mt-0.5 line-clamp-1">
                                          in {job.location}
                                        </p>
                                      </div>
                                      <div className="flex flex-row items-center gap-2">
                                        <span className="text-sm font-semibold text-[#AEAEB2]">
                                          {job.budget}
                                        </span>
                                        <ChevronRight className="h-4 w-4 text-[#3A3A3C] group-hover:text-[#8E8E93] transition-colors" />
                                      </div>
                                    </div>
                                  </motion.article>
                                ))}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Right column: Claim Your Credibility */}
                        {showExpressBookingFeatures && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-2xl bg-[#111111] flex items-center justify-center border border-white/10 shadow-[0_8px_26px_rgba(0,0,0,0.8)]">
                                <ShieldCheck className="h-4 w-4 text-[#FACC15]" />
                              </div>
                              <div className="flex flex-col">
                                <h2 className="text-[11px] font-semibold text-[#F5F5F7] tracking-[0.24em] uppercase">
                                  Claim Your Credibility
                                </h2>
                                <p className="text-[11px] text-[#8E8E93]">
                                  Just a few quick details.
                                </p>
                              </div>
                            </div>
                            {/* {credSubmitted && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCredSubmitted(false);
                                  setCredStep(1);
                                }}
                                className="text-[11px] font-medium text-[#F33363] hover:underline"
                              >
                                Edit
                              </button>
                            )} */}
                          </div>

                          <div className="flex flex-col justify-between rounded-[2rem] bg-gradient-to-b from-[#111111] via-[#050506] to-black border border-[#27272A] px-6 py-5 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
                            <AnimatePresence mode="wait">
                              {isCredibilityLoading ? (
                                <motion.div
                                  key="cred-loading"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  transition={{ duration: 0.22, ease: 'easeOut' }}
                                  className="space-y-3"
                                >
                                  <p className="text-sm text-[#8E8E93]">Loading credibility...</p>
                                </motion.div>
                              ) : credSubmitted ? (
                                <motion.div
                                  key="cred-summary"
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -8 }}
                                  transition={{ duration: 0.22, ease: 'easeOut' }}
                                  className="space-y-4"
                                >
                                  <p className="text-sm text-[#E5E5EA]">
                                    Thanks! Here’s what you shared:
                                  </p>
                                  <div className="space-y-2 text-sm text-[#E5E5EA]">
                                    <div className="flex justify-between gap-4">
                                      <span className="text-[#8E8E93]">First paid gig</span>
                                      <span className="font-semibold">{credYear ?? 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-[#8E8E93]">Where</span>
                                      <span className="font-semibold truncate max-w-[140px] text-right">
                                        {credCity?.trim() ? credCity : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-[#8E8E93]">Event type</span>
                                      <span className="font-semibold truncate max-w-[140px] text-right">
                                        {(credEventTypeDisplay ?? credEventType)?.trim()
                                          ? credEventTypeDisplay ?? credEventType
                                          : 'N/A'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between gap-4">
                                      <span className="text-[#8E8E93]">Performed for</span>
                                      <span className="font-semibold truncate max-w-[140px] text-right">
                                        {credClient.trim() ? credClient : 'N/A'}
                                      </span>
                                    </div>
                                  </div>
                                </motion.div>
                              ) : (
                                <motion.div
                                  key={`cred-step-${credStep}`}
                                  initial={{ opacity: 0, y: 8 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -8 }}
                                  transition={{ duration: 0.22, ease: 'easeOut' }}
                                  className="space-y-3"
                                >
                                  {/* Top navigation row: step indicator + chevrons */}
                                  <div className="flex items-center justify-between gap-3 mb-1">
                                    <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]">
                                      <span>
                                        Step {credStep} of 4
                                      </span>
                                      <div className="w-16 h-1 rounded-full bg-white/5 overflow-hidden">
                                        <div
                                          className="h-full rounded-full bg-gradient-to-r from-[#F33363] to-[#FACC15]"
                                          style={{ width: `${(credStep / 4) * 100}%` }}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setCredStep((s) => Math.max(1, s - 1))}
                                        disabled={credStep === 1}
                                        className="p-1.5 rounded-full border border-white/10 bg-black/40 text-xs text-[#9CA3AF] disabled:opacity-30 disabled:cursor-default"
                                      >
                                        <ChevronLeft className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setCredStep((s) => Math.min(4, s + 1))}
                                        disabled={credStep === 4}
                                        className="p-1.5 rounded-full border border-white/10 bg-black/40 text-xs text-[#9CA3AF] disabled:opacity-30 disabled:cursor-default"
                                      >
                                        <ChevronRight className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Step content */}
                                  {credStep === 1 && (
                                    <div className="space-y-3">
                                      <p className="text-sm text-[#E5E5EA]">
                                        Hey Star! When was your first paid gig?
                                      </p>
                                      <div className="relative">
                                        <select
                                          value={credYear ?? ''}
                                          onChange={(e) => {
                                            const value = e.target.value || null;
                                            setCredYear(value);
                                            if (value) {
                                              setCredStep(2);
                                            }
                                          }}
                                          className="w-full appearance-none rounded-2xl bg-[#050506] border border-[#27272A] px-4 py-3 text-sm text-[#E5E5EA] focus:outline-none focus:ring-2 focus:ring-[#F33363]/60 focus:border-[#F33363]/60 pr-10"
                                        >
                                          <option value="" disabled>
                                            Select year
                                          </option>
                                          {Array.from({ length: 31 }).map((_, idx) => {
                                            const year = new Date().getFullYear() - idx;
                                            return (
                                              <option key={year} value={year}>
                                                {year}
                                              </option>
                                            );
                                          })}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                                          <ChevronDown className="h-4 w-4 text-[#6B7280]" />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {credStep === 2 && (
                                    <div className="space-y-3">
                                      <p className="text-sm text-[#E5E5EA]">
                                        Where did you perform?
                                      </p>
                                      {credCityMode === 'select' ? (
                                        <div className="relative">
                                          <select
                                            value={credCity ?? ''}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (value === 'other') {
                                                setCredCityMode('custom');
                                                setCredCity('');
                                              } else {
                                                setCredCity(value);
                                                setCredStep(3);
                                              }
                                            }}
                                            className="w-full appearance-none rounded-2xl bg-[#050506] border border-[#27272A] px-4 py-3 text-sm text-[#E5E5EA] focus:outline-none focus:ring-2 focus:ring-[#F33363]/60 focus:border-[#F33363]/60 pr-10"
                                          >
                                            <option value="" disabled>
                                              Select city
                                            </option>
                                            {credibilityCities.map((city) => (
                                              <option key={city} value={city}>
                                                {city}
                                              </option>
                                            ))}
                                            <option value="other">Not listed here</option>
                                          </select>
                                          <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                                            <ChevronDown className="h-4 w-4 text-[#6B7280]" />
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          <input
                                            type="text"
                                            value={credCity ?? ''}
                                            onChange={(e) => setCredCity(e.target.value)}
                                            className="w-full rounded-2xl bg-[#050506] border border-[#27272A] px-4 py-3 text-sm text-[#E5E5EA] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#F33363]/60 focus:border-[#F33363]/60"
                                            placeholder="Type your city"
                                          />
                                          <div className="flex justify-end">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                if (credCity && credCity.trim()) {
                                                  setCredStep(3);
                                                }
                                              }}
                                              className="inline-flex items-center gap-2 rounded-full bg-[#F33363] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-40"
                                              disabled={!credCity || !credCity.trim()}
                                            >
                                              Next
                                              <ChevronRight className="h-3.5 w-3.5" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {credStep === 3 && (
                                    <div className="space-y-3">
                                      <p className="text-sm text-[#E5E5EA]">
                                        What was the type of the event?
                                      </p>
                                      <div className="relative">
                                        <select
                                          value={credEventType ?? ''}
                                          onChange={(e) => {
                                            const value = e.target.value || null;
                                            setCredEventType(value);
                                            if (value) {
                                              const match = EVENT_TYPES.find((t) => t.e_slug === value);
                                              setCredEventTypeDisplay(match?.event_name ?? value);
                                            } else {
                                              setCredEventTypeDisplay(null);
                                            }
                                            if (value) {
                                              setCredStep(4);
                                            }
                                          }}
                                          className="w-full appearance-none rounded-2xl bg-[#050506] border border-[#27272A] px-4 py-3 text-sm text-[#E5E5EA] focus:outline-none focus:ring-2 focus:ring-[#F33363]/60 focus:border-[#F33363]/60 pr-10"
                                        >
                                          <option value="" disabled>
                                            Select event type
                                          </option>
                                          {EVENT_TYPES.map((type) => (
                                            <option key={type.e_slug} value={type.e_slug}>
                                              {type.event_name}
                                            </option>
                                          ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                                          <ChevronDown className="h-4 w-4 text-[#6B7280]" />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {credStep === 4 && (
                                    <div className="space-y-3">
                                      <p className="text-sm text-[#E5E5EA]">
                                        For whom did you perform?
                                      </p>
                                      <input
                                        type="text"
                                        value={credClient}
                                        onChange={(e) => setCredClient(e.target.value)}
                                        className="w-full rounded-2xl bg-[#050506] border border-[#27272A] px-4 py-3 text-sm text-[#E5E5EA] placeholder:text-[#6B7280] focus:outline-none focus:ring-2 focus:ring-[#F33363]/60 focus:border-[#F33363]/60"
                                        placeholder="Client / brand name"
                                      />
                                      <div className="flex justify-end">
                                        <button
                                          type="button"
                                          onClick={() => void handleCredibilitySubmit()}
                                          disabled={
                                            isCredibilitySubmitting ||
                                            !credYear ||
                                            !credCity?.trim() ||
                                            !credEventType ||
                                            !credClient.trim()
                                          }
                                          className="inline-flex items-center gap-2 rounded-full bg-[#F33363] px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-40"
                                        >
                                          {isCredibilitySubmitting ? 'Submitting...' : 'Submit'}
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                        )}
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 lg:auto-rows-fr xl:gap-8 gap-4 mt-8'>
                        {/* Category performance graph card */}
                        <div className="w-full rounded-[2.5rem] h-full px-2 py-2 md:py-2 md:px-0 flex flex-col md:col-span-2 xl:col-span-1">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-2xl bg-[#111111] flex items-center justify-center border border-white/10 shadow-[0_8px_26px_rgba(0,0,0,0.8)]">
                                <TrendingUp className="h-4 w-4 text-emerald-400" />
                              </div>
                              <div className="flex flex-col">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#F5F5F7]">
                                  Category Performance
                                </p>
                                <p className="text-[11px] text-[#8E8E93]">
                                  How your earnings trend over time.
                                </p>

                              </div>
                            </div>

                          </div>
                          <div className='flex flex-row items-center justify-between'>
                            <div className="flex items-baseline gap-2">
                              <p className="text-2xl sm:text-3xl font-extrabold text-white">
                                {categoryPerformanceTotalBookings !== null
                                  ? categoryPerformanceTotalBookings.toLocaleString()
                                  : '—'}
                              </p>
                              <p className="text-xs sm:text-sm text-[#8E8E93]">
                                Confirmed Bookings
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <select
                                className="h-8 rounded-md border border-[#3A3A3C] bg-[#1C1C1E] px-2 text-xs text-white outline-none focus-visible:ring-2 focus-visible:ring-[#F33363]"
                                value={categoryPerformancePeriod}
                                onChange={(e) =>
                                  setCategoryPerformancePeriod(
                                    e.target.value as CategoryPerformancePeriod
                                  )
                                }
                                aria-label="Category performance time range"
                              >
                                {CATEGORY_PERFORMANCE_PERIOD_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                          </div>

                          <div className="mt-3 h-40 sm:h-48 lg:h-full w-full">
                            {categoryPerformanceLoading ? (
                              <div className="w-full h-full flex items-center justify-center text-sm text-[#8E8E93]">
                                Loading...
                              </div>
                            ) : categoryPerformanceError ? (
                              <div className="w-full h-full flex items-center justify-center text-sm text-[#F33363]">
                                {categoryPerformanceError}
                              </div>
                            ) : categoryPerformanceChartData.length === 0 ? (
                              <div className="w-full h-full flex items-center justify-center text-sm text-[#8E8E93]">
                                No data available for this period.
                              </div>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <AreaChart
                                  data={categoryPerformanceChartData}
                                  margin={{ top: 8, right: 0, left: -32, bottom: 8 }}
                                >
                                  <defs>
                                    <linearGradient
                                      id="categoryPerformanceGradient"
                                      x1="0"
                                      y1="0"
                                      x2="0"
                                      y2="1"
                                    >
                                      <stop offset="0%" stopColor="#FACC15" stopOpacity={0.4} />
                                      <stop offset="100%" stopColor="#FACC15" stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#27272A"
                                    vertical={false}
                                  />
                                  <XAxis
                                    dataKey="label"
                                    stroke="#71717A"
                                    fontSize={11}
                                    tickLine={false}
                                    axisLine={false}
                                  />
                                  <YAxis
                                    stroke="#71717A"
                                    fontSize={11}
                                    tickLine={false}
                                    axisLine={false}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: '#09090B',
                                      border: '1px solid #27272A',
                                      borderRadius: '12px',
                                      padding: '8px 10px',
                                      fontSize: '11px',
                                      color: '#fff',
                                    }}
                                    cursor={{ stroke: '#3A3A3C' }}
                                    formatter={(value) => [
                                      typeof value === 'number'
                                        ? value.toLocaleString('en-IN')
                                        : String(value ?? ''),
                                      'Confirmed Bookings',
                                    ]}
                                    labelFormatter={(label) => `${label}`}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#FACC15"
                                    fill="url(#categoryPerformanceGradient)"
                                    strokeWidth={2.4}
                                  />
                                </AreaChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>

                        {/* Right: AI Ranking Podium (spans two columns on xl) */}
                        <div className="flex flex-col gap-4 md:col-span-2 xl:col-span-2">
                          {/* AI Ranking Podium */}
                          <div className="relative rounded-[2.5rem] bg-gradient-to-b from-[#151516] via-[#050506] to-black border border-[#3A3A3C]/80 px-4 py-4 md:px-8 md:py-8 shadow-[0_0_80px_rgba(0,0,0,0.9)] overflow-hidden flex-1">
                            {/* Subtle background glow */}
                            {/* <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(248,113,113,0.18),_transparent_60%),radial-gradient(circle_at_bottom,_rgba(59,130,246,0.16),_transparent_65%)] opacity-80" /> */}

                            <div className="relative z-10 space-y-10 w-full">
                              {/* Header */}
                              <div className="flex flex-col items-center gap-2">
                                <p className="text-xl font-black tracking-[0.32em] uppercase text-[#8E8E93] mb-4">
                                  Powered By <span className="text-[#F33363]">AI</span>
                                </p>
                                <div className=" flex w-full max-w-xs items-center justify-between text-[11px] font-semibold uppercase tracking-[0.3em]">
                                  <button
                                    type="button"
                                    onClick={() => setActiveRankTab('regional')}
                                    className={cn(
                                      'transition-colors',
                                      'cursor-pointer',
                                      activeRankTab === 'regional' ? 'text-white' : 'text-[#636366]',
                                    )}
                                  >
                                    Regional Rank
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setActiveRankTab('air')}
                                    className={cn(
                                      'transition-colors',
                                      'cursor-pointer',
                                      activeRankTab === 'air' ? 'text-white' : 'text-[#636366]',
                                    )}
                                  >
                                    AIR
                                  </button>
                                </div>
                                {/* Top light bars */}
                                <div className=" flex w-full max-w-xs items-center justify-between gap-6">
                                  <div
                                    className={cn(
                                      'h-0.5 flex-1 rounded-full bg-gradient-to-r from-transparent to-transparent',
                                      activeRankTab === 'regional' ? 'via-[#F33363]/80' : 'via-[#636366]/70',
                                    )}
                                  />
                                  <div
                                    className={cn(
                                      'h-0.5 flex-1 rounded-full bg-gradient-to-r from-transparent to-transparent',
                                      activeRankTab === 'air' ? 'via-[#F33363]/80' : 'via-[#636366]/70',
                                    )}
                                  />
                                </div>
                              </div>

                              {/* Podium + rank list layout (stacked on mobile, row on xl) */}
                              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Podium section */}
                                <div className="flex-1 grid grid-cols-3 gap-3 sm:gap-6 justify-center items-end">
                                  {/* Second */}
                                  <div className="flex flex-col items-center gap-0">
                                    <div className="flex flex-col items-center gap-3">
                                      <div className="relative flex h-12  w-12 items-center justify-center rounded-full border border-[#3A3A3C] bg-gradient-to-b from-[#1C1C1E] to-[#050507] shadow-[0_0_30px_rgba(96,165,250,0.3)]">
                                        <div
                                          className="h-10 w-10 overflow-hidden rounded-full bg-[#1C1C1E] bg-cover bg-center"
                                          style={{
                                            backgroundImage:
                                              activeRankTab === 'regional' && regionalPodium.second?.profile_pic
                                                ? `url(${regionalPodium.second.profile_pic})`
                                                : undefined,
                                          }}
                                        />
                                      </div>
                                      <div className="flex flex-col items-center gap-0">
                                        <span className="mb-1 text-xs font-semibold text-white/90 text-center">
                                          {activeRankTab === 'regional'
                                            ? toTwoWordName(regionalPodium.second?.artist_name)
                                            : 'Artist Name'}
                                        </span>
                                        <div className="mb-2 flex flex-col items-center text-[11px] font-medium text-[#8E8E93]">
                                          <span>
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.second?.category ?? 'Category')
                                              : 'Category'}
                                          </span>
                                          <span className="rounded-md bg-[#272729]/20 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.second?.plan ?? 'Plan')
                                              : 'Plan'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>


                                    <div className="flex h-20 w-full flex-col items-center justify-center  bg-gradient-to-t from-[#151516] to-[#050506] border border-[#3A3A3C]/20">

                                      <div className="flex  items-center justify-center rounded-3xl  text-4xl font-black uppercase tracking-[0.26em] text-white/40 bg-none" >
                                        {activeRankTab === 'regional'
                                          ? (regionalPodium.second?.rank ?? 2)
                                          : 2}
                                      </div>
                                    </div>
                                  </div>

                                  {/* First */}
                                  <div className="flex flex-col items-center gap-0">
                                    <div className="flex flex-col items-center gap-3">
                                      <div className="relative flex h-16 w-16 items-center justify-center rounded-full border border-[#F33363]/40 bg-gradient-to-t from-[#3F1018] via-[#1C1C1E] to-[#050506] shadow-[0_0_40px_rgba(243,51,99,0.5)]">
                                        <div
                                          className="h-18 w-18 overflow-hidden rounded-full bg-[#1C1C1E] bg-cover bg-center"
                                          style={{
                                            backgroundImage:
                                              activeRankTab === 'regional' && regionalPodium.first?.profile_pic
                                                ? `url(${regionalPodium.first.profile_pic})`
                                                : undefined,
                                          }}
                                        />
                                        <div className="pointer-events-none absolute inset-0 rounded-full border border-[#F33363]/40 ring-2 ring-[#F33363]/40" />
                                      </div>
                                      <div className="flex flex-col items-center gap-0">

                                        <span className="mb-1 text-sm font-semibold text-white text-center">
                                          {activeRankTab === 'regional'
                                            ? toTwoWordName(regionalPodium.first?.artist_name)
                                            : 'Artist Name'}
                                        </span>
                                        <div className="mb-3 flex flex-col items-center text-[11px] font-medium text-[#D1D1D6]">
                                          <span>
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.first?.category ?? 'Category')
                                              : 'Category'}
                                          </span>
                                          <span className="rounded-md bg-[#3B0B16]/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.first?.plan ?? 'Plan')
                                              : 'Plan'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>


                                    <div className="flex h-36 w-full flex-col items-center justify-center  bg-gradient-to-t from-[#050506] via-[#1C1C1E] to-[#3F1018]   ">

                                      <div className="flex  items-center justify-center rounded-3xl  text-4xl font-black uppercase tracking-[0.26em] text-white/40 bg-none" >
                                        {activeRankTab === 'regional'
                                          ? (regionalPodium.first?.rank ?? 1)
                                          : 1}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Third */}
                                  <div className="flex flex-col items-center gap-0">
                                    <div className="flex flex-col items-center gap-3">
                                      <div className="relative flex h-12  w-12 items-center justify-center rounded-full border border-[#3A3A3C] bg-gradient-to-b from-[#1C1C1E] to-[#050507] shadow-[0_0_30px_rgba(96,165,250,0.3)]">
                                        <div
                                          className="h-10 w-10 overflow-hidden rounded-full bg-[#1C1C1E] bg-cover bg-center"
                                          style={{
                                            backgroundImage:
                                              activeRankTab === 'regional' && regionalPodium.third?.profile_pic
                                                ? `url(${regionalPodium.third.profile_pic})`
                                                : undefined,
                                          }}
                                        />
                                      </div>
                                      <div className="flex flex-col items-center gap-0">
                                        <span className="mb-1 text-xs font-semibold text-white/90 text-center">
                                          {activeRankTab === 'regional'
                                            ? toTwoWordName(regionalPodium.third?.artist_name)
                                            : 'Artist Name'}
                                        </span>
                                        <div className="mb-2 flex flex-col items-center text-[11px] font-medium text-[#8E8E93]">
                                          <span>
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.third?.category ?? 'Category')
                                              : 'Category'}
                                          </span>
                                          <span className="rounded-md bg-[#272729]/20 px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                                            {activeRankTab === 'regional'
                                              ? (regionalPodium.third?.plan ?? 'Plan')
                                              : 'Plan'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="flex h-12 w-full flex-col items-center justify-center bg-gradient-to-t from-[#151516] to-[#050506] border border-[#3A3A3C]/20">

                                      <div className="flex  items-center justify-center rounded-3xl  text-4xl font-black uppercase tracking-[0.26em] text-white/40 bg-none" >
                                        {activeRankTab === 'regional'
                                          ? (regionalPodium.third?.rank ?? 3)
                                          : 3}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col justify-between items-center">
                                  {/* Rank list */}
                                  <div className="mt-7 xl:mt-0 flex flex-col space-y-3 bg-gradient-to-b from-[#3F1018] via-black/10 to-[#050506] p-4 w-full">
                                    {(activeRankTab === 'regional'
                                      ? regionalRankList
                                      : [8, 9, 10].map((rank) => ({
                                        rank,
                                        artist_name: 'Artist Name',
                                        category: 'Category',
                                        plan: 'Plan',
                                        profile_pic: null,
                                      }))).map((rankEntry) => (
                                        <div
                                          key={rankEntry.rank}
                                          className="flex items-center justify-between gap-3 border-b pb-2 border-[#3A3A3C]/70"
                                        >
                                          <div className="flex items-center gap-3">
                                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2C2C2E] text-xs font-semibold text-[#AEAEB2]">
                                              {rankEntry.rank}
                                            </span>
                                            <div
                                              className="relative h-9 w-9 overflow-hidden rounded-full bg-[#2C2C2E] bg-cover bg-center"
                                              style={{
                                                backgroundImage: rankEntry.profile_pic
                                                  ? `url(${rankEntry.profile_pic})`
                                                  : undefined,
                                              }}
                                            />
                                            <div className="flex flex-col">
                                              <span className="text-sm font-semibold text-white">
                                                {rankEntry.artist_name}
                                              </span>
                                              <div className="flex items-center gap-2 text-[11px] font-medium text-[#8E8E93]">
                                                <span>{rankEntry.category ?? 'Category'}</span>
                                                <span className="rounded-md bg-[#272729] px-2 py-0.5 text-[10px] font-semibold text-amber-400">
                                                  {rankEntry.plan ?? 'Plan'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>

                                  {/* CTA */}
                                  {/* <button
                                    type="button"
                                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-t from-[#3F1018]  to-[#050506] border border-[#3A3A3C]/20  py-4 text-sm font-black uppercase tracking-[0.26em] text-white "
                                  >
                                    <span className="">Ways To Climb Rank</span>
                                  </button> */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Elite Members Club - moved below graph + AI grid */}
                        <div className=" relative flex flex-col items-center justify-center w-full rounded-[2.5rem] bg-gradient-to-b from-[#050506] via-black to-black border border-[#3A3A3C]/80 px-6 py-7 sm:px-8 sm:py-8 shadow-[0_0_80px_rgba(0,0,0,0.9)] overflow-hidden">
                          <div className={cn('relative z-10 flex flex-col items-center text-center space-y-4 w-full')}>
                            <h3 className="text-xl font-black tracking-[0.2em] text-[#8E8E93] uppercase">
                              Elite Members’ Club
                            </h3>

                            {/* Background graphic with avatar */}
                            <div
                              className={cn(
                                'relative w-full mx-auto aspect-[350/228] transition-opacity duration-500 cursor-pointer',
                                eliteIsFading ? 'opacity-0' : 'opacity-100'
                              )}
                              role="button"
                              tabIndex={0}
                              onClick={handleEliteMemberClick}
                              onMouseEnter={() => setEliteIsHoverPaused(true)}
                              onMouseLeave={() => setEliteIsHoverPaused(false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') handleEliteMemberClick();
                              }}
                            >
                              <img
                                src={eliteGroupBackground}
                                alt="Elite members club background"
                                className="w-full h-full object-contain"
                              />
                              <div className="pointer-events-none absolute top-20 md:top-5 inset-0 flex items-center justify-center">
                                <div
                                  className="h-[20vw] w-[20vw] md:h-[15vw] md:w-[15vw] lg:h-28 lg:w-28 xl:h-40 xl:w-40 rounded-full border-4 border-[#F33363] shadow-[0_0_40px_rgba(243,51,99,0.7)] overflow-hidden bg-[#1C1C1E]"
                                  style={{
                                    backgroundImage: activeEliteMember?.profile_pic
                                      ? `url(${activeEliteMember.profile_pic})`
                                      : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                  }}
                                />
                              </div>
                            </div>

                            {/* Name & location */}
                            <div
                              className={cn(
                                'space-y-1 transition-opacity duration-500 cursor-pointer',
                                eliteIsFading ? 'opacity-0' : 'opacity-100'
                              )}
                              role="button"
                              tabIndex={0}
                              onClick={handleEliteMemberClick}
                              onMouseEnter={() => setEliteIsHoverPaused(true)}
                              onMouseLeave={() => setEliteIsHoverPaused(false)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') handleEliteMemberClick();
                              }}
                            >
                              <p className="text-sm sm:text-base font-semibold text-white">
                                {activeEliteMember?.professional_name || 'Loading...'}
                              </p>
                              <p className="text-xs sm:text-sm font-medium text-[#8E8E93]">
                                {activeEliteMember?.location || ''}
                              </p>
                            </div>

                            {/* CTA */}
                            <button
                              type="button"
                              onClick={() => navigate('/membership')}
                              className="mt-4 inline-flex items-center justify-center rounded-full bg-[#FF2D55] px-8 py-3 text-sm font-semibold text-white shadow-[0_0_35px_rgba(255,45,85,0.7)]"
                            >
                              Join the Elitehood
                            </button>
                          </div>
                        </div>




                      </div>
                    </div>
                  )}
                {/* {activeNav === 'assistant' && (
                  <div className="w-full h-full md:px-2">
                    <AssistantMainView />
                  </div>
                )} */}
                {activeNav === 'profile' && location.pathname === '/profile' && (
                  <div className="w-full">
                    {/* Premium Profile Hero */}
                    <div className="relative my-12">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative rounded-[3rem] bg-gradient-to-b from-[#1C1C1E] to-[#0B0B0D]  overflow-hidden p-8 lg:p-12 shadow-2xl"
                      >
                        {/* Ethereal Background Glow */}
                        <div className="absolute -top-24 -left-24 w-80 h-80 bg-[#F33363]/5 rounded-full blur-[100px]" />
                        <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-purple-500/5 rounded-full blur-[100px]" />

                        <div className="relative z-10 flex flex-col items-center">
                          {/* Meta Info */}
                          <div className="w-full flex justify-between items-start mb-12">
                            {artistLocationLabel ? (
                              <div className="space-y-1">
                                <p className="text-[10px] font-black text-[#8E8E93] uppercase tracking-[0.2em]">Artist Location</p>
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-[#F33363]" />
                                  <span className="text-sm font-bold text-white">{artistLocationLabel}</span>
                                </div>
                              </div>
                            ) : null}
                            <div className="flex flex-col items-end gap-0.5 px-4 py-2 bg-white/5 rounded-2xl">
                              <span className="text-[10px] font-black text-[#8E8E93] uppercase tracking-wider">Artist Identity</span>
                              {artistCategoryName ? (
                                <span className="text-xs font-bold text-white uppercase tracking-wide">{artistCategoryName}</span>
                              ) : (
                                <span className="text-xs text-[#8E8E93]">—</span>
                              )}
                            </div>
                          </div>

                          {/* Avatar & Identity - click to update photo (no progress ring) */}
                          <button
                            type="button"
                            onClick={() => setIsProfilePhotoModalOpen(true)}
                            className={cn(
                              'relative mb-8 inline-block rounded-full border-4 border-[#0B0B0D] focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:ring-offset-2 focus:ring-offset-[#0B0B0D]',
                              profilePhotoNeedsAttention
                                ? 'shadow-[0_0_50px_rgba(251,191,36,0.28)] ring-2 ring-amber-400/35 ring-offset-2 ring-offset-[#0B0B0D]'
                                : 'shadow-[0_0_50px_rgba(243,51,99,0.2)]'
                            )}
                          >
                            <div className="w-32 h-32 lg:w-44 lg:h-44 rounded-full overflow-hidden bg-[#2C2C2E] flex items-center justify-center">
                              {profilePic ? (
                                <img src={profilePic} alt={userName} className="w-full h-full object-cover" />
                              ) : (
                                <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
                              )}
                            </div>
                            <div className="absolute -bottom-2 -right-2 z-[2] w-10 h-10 rounded-2xl bg-[#F33363] border-4 border-[#0B0B0D] flex items-center justify-center">
                              <ImagePlus className="h-5 w-5 text-white" />
                            </div>
                            {profilePhotoNeedsAttention ? (
                              <ProfileIncompleteCornerPing className="-right-4 top-0 z-[3] md:-right-2 md:top-2" />
                            ) : null}
                          </button>

                          <h1 className="text-3xl lg:text-5xl font-black  tracking-tighter mb-4 text-center bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent">{userName}</h1>
                          <p className="text-xs text-[#8E8E93] font-medium  text-center uppercase tracking-widest">Your website link</p>
                          <a
                            href={artistPortalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-2 px-6 py-1 rounded-2xl transition-all duration-300"
                          >
                            <span className="text-xs font-bold text-blue-500 group-hover:text-white transition-colors">starclinch.com/{artistSlug || 'artist-portal'}</span>
                            <ExternalLink className="h-3.5 w-3.5 text-blue-500 group-hover:text-[#F33363]" />
                          </a>
                        </div>

                        {/* Decorative Wave - Refined */}
                        <div className="absolute bottom-0 inset-x-0 opacity-20 pointer-events-none">
                          <img src={profileWave} alt="" className="w-full h-24 object-cover" />
                        </div>
                      </motion.div>
                    </div>

                    {/* Profile Action Grid */}
                    <div className="space-y-8">
                      <div className="relative z-[200] flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="space-y-1">
                          <h2 className="text-2xl font-black text-white tracking-tight">Success Checklist</h2>
                          <p className="text-sm text-[#8E8E93] font-medium">
                            Complete these blocks to reach <span className="text-white">100% visiblity</span>.
                          </p>
                        </div>
                        {showProfileCompletionBar ? (
                          <div
                            ref={profileCompletionBarRef}
                            className="relative w-full shrink-0 sm:w-auto sm:max-w-[min(100%,20rem)]"
                          >
                            <div className="hidden lg:block">
                              <div
                                onMouseEnter={openProfileCompletionDesktop}
                                onMouseLeave={scheduleCloseProfileCompletionDesktop}
                              >
                                <button
                                  type="button"
                                  className="w-full rounded-2xl border border-[#3A3A3C]/60 bg-white/[0.03] px-4 py-3 text-left transition-colors hover:border-[#3A3A3C] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                                  aria-expanded={profileCompletionHover}
                                  aria-haspopup="dialog"
                                >
                                  <ProfileCompletionMiniBar state={profileCompletion} size="comfortable" />
                                </button>
                              </div>
                            </div>
                            <div className="relative z-[60] lg:hidden">
                              <button
                                type="button"
                                onClick={() => setProfileCompletionMobileOpen((o) => !o)}
                                className="relative z-10 w-full rounded-2xl border border-[#3A3A3C]/60 bg-white/[0.03] px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                                aria-expanded={profileCompletionMobileOpen}
                                aria-haspopup="dialog"
                              >
                                <ProfileCompletionMiniBar state={profileCompletion} size="comfortable" />
                              </button>
                              {profileCompletionMobileOpen ? (
                                <div
                                  className="absolute left-0 right-0 top-full z-[100] mt-2 max-h-[min(70vh,24rem)] overflow-y-auto rounded-2xl border border-[#3A3A3C]/60 bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] p-4 shadow-2xl"
                                  role="dialog"
                                  aria-label="Profile blocks still needed"
                                >
                                  <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-[#8E8E93]">
                                    Still needed
                                  </p>
                                  <ProfileCompletionListContent
                                    state={profileCompletion}
                                    onNavigate={handleProfileCompletionNavigate}
                                  />
                                </div>
                              ) : null}
                            </div>
                            {profileCompletionHover &&
                              profileCompletionPanelPos &&
                              typeof document !== 'undefined'
                              ? createPortal(
                                <div
                                  ref={profileCompletionDesktopPanelRef}
                                  className="hidden lg:block fixed z-[60]"
                                  style={{
                                    top: `${profileCompletionPanelPos.top}px`,
                                    left: `${profileCompletionPanelPos.left}px`,
                                  }}
                                  onMouseEnter={openProfileCompletionDesktop}
                                  onMouseLeave={scheduleCloseProfileCompletionDesktop}
                                >
                                  <div
                                    className="absolute -top-3 left-0 right-0 h-3"
                                    aria-hidden
                                  />
                                  <div className="w-[min(100vw-1.5rem,17.5rem)] rounded-2xl border border-[#3A3A3C]/60 bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] p-4 shadow-2xl">
                                    <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-[#8E8E93]">
                                      Still needed
                                    </p>
                                    <ProfileCompletionListContent
                                      state={profileCompletion}
                                      onNavigate={handleProfileCompletionNavigate}
                                    />
                                  </div>
                                </div>,
                                document.body
                              )
                              : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="relative z-0 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                        {[
                          { id: 'gallery', label: 'Gallery', sub: 'Showcase your work', icon: ImagePlus, status: 'High Impact', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'personal-details', label: 'Personal Details', sub: 'Verify your details', icon: User, status: 'Required', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'performance-details', label: 'Performance Details', sub: 'Set your pricing', icon: FolderPlus, status: 'Critical', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'usp', label: 'Unique Sell Point', sub: 'Stand out from crowd', icon: Sparkles, status: 'Boost', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'story', label: 'Story', sub: 'Share your journey', icon: BookOpen, status: 'Loyalty', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'awards-achievements', label: 'Awards & Achievements', sub: 'Proven track record', icon: Star, status: 'Trust', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'past-performances', label: 'Past Performances', sub: 'Event history', icon: Calendar, status: 'Proof', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                          { id: 'social-media', label: 'Social Media', sub: 'Connect Instagram', icon: Instagram, status: 'Discovery', color: 'text-gray-500', bg: 'bg-gray-500/10' },
                        ].map((block, i) => {
                          const blockNeedsAttention =
                            profileCompletion.isReady &&
                            !profileCompletion.isIndeterminate &&
                            checklistBlockNeedsAttention(block.id, profileCompletion.segmentComplete);
                          return (
                            <motion.button
                              key={block.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                              onClick={() => navigate(`/profile/${block.id}`)}
                              className={cn(
                                'group relative flex flex-col overflow-hidden text-left rounded-[2rem] border p-5 transition-all duration-300 md:p-6 lg:p-7',
                                'bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] hover:bg-[#2C2C2E]',
                                blockNeedsAttention
                                  ? 'border-amber-400/25 shadow-[inset_0_1px_0_0_rgba(251,191,36,0.12)] hover:border-amber-400/35'
                                  : 'border-[#3A3A3C]/80 hover:border-[#3A3A3C]'
                              )}
                            >
                              {blockNeedsAttention ? (
                                <div
                                  className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/45 to-transparent"
                                  aria-hidden
                                />
                              ) : null}
                              {blockNeedsAttention ? <ProfileIncompleteCornerPing /> : null}
                              <div className="flex items-start justify-between md:mb-6 mb-4">
                                <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center ", block.bg)}>
                                  <block.icon className={cn("h-6 w-6", block.color)} />
                                </div>
                                {/* <div className="flex items-center gap-1.5 px-2 py-0.5 bg-white/5 rounded-full ">
                                <div className={cn("w-1 h-1 rounded-full", block.color === 'text-[#F33363]' ? 'bg-[#F33363]' : block.color.replace('text-', 'bg-'))} />
                                <span className="text-[9px] font-black text-[#8E8E93] uppercase tracking-widest">{block.status}</span>
                              </div> */}
                              </div>

                              <div className="mt-auto">
                                <h3
                                  className={cn(
                                    'text-lg font-black bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent transition-colors',
                                    blockNeedsAttention
                                      ? 'group-hover:text-amber-400'
                                      : 'group-hover:text-[#F33363]'
                                  )}
                                >
                                  {block.label}
                                </h3>
                                <p className="text-xs text-[#8E8E93]/80 font-medium mt-1">{block.sub}</p>
                              </div>

                              <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1">
                                <ChevronRight
                                  className={cn(
                                    'h-5 w-5',
                                    blockNeedsAttention ? 'text-amber-400' : 'text-[#F33363]'
                                  )}
                                />
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>

                      {/* Event Essentials Section */}
                      <div className="mt-12 space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <h2 className="text-2xl font-black bg-gradient-to-b from-white to-gray-400 bg-clip-text text-transparent tracking-tight">Event Essentials</h2>
                            <p className="text-sm text-[#8E8E93] font-medium">Manage your professional documents and pricing</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 pb-24">
                          <motion.button
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => navigate('/profile/event-essentials')}
                            className={cn(
                              'group relative flex flex-col overflow-hidden text-left rounded-[2rem] border p-5 transition-all duration-300 md:p-6 lg:p-7',
                              'bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)] hover:bg-[#2C2C2E]',
                              eventPricingNeedsAttention
                                ? 'border-amber-400/25 shadow-[inset_0_1px_0_0_rgba(251,191,36,0.12)] hover:border-amber-400/35'
                                : 'border-[#3A3A3C]/80 hover:border-[#3A3A3C]'
                            )}
                          >
                            {eventPricingNeedsAttention ? (
                              <div
                                className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/45 to-transparent"
                                aria-hidden
                              />
                            ) : null}
                            {eventPricingNeedsAttention ? <ProfileIncompleteCornerPing /> : null}
                            <div className="flex items-start justify-between md:mb-6 mb-4">
                              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-blue-500/10">
                                <BarChart3 className="h-6 w-6 text-blue-500" />
                              </div>

                            </div>

                            <div className="mt-auto">
                              <h3
                                className={cn(
                                  'text-lg font-black text-white transition-colors line-clamp-1',
                                  eventPricingNeedsAttention
                                    ? 'group-hover:text-amber-400'
                                    : 'group-hover:text-[#F33363]'
                                )}
                              >
                                Event Pricing & Billing
                              </h3>
                              <p className="text-sm text-[#8E8E93] font-medium mt-1">Pricing, bank account & GST details</p>
                            </div>

                            <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all group-hover:translate-x-1">
                              <ChevronRight
                                className={cn(
                                  'h-5 w-5',
                                  eventPricingNeedsAttention ? 'text-amber-400' : 'text-[#F33363]'
                                )}
                              />
                            </div>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {activeNav === 'profile' && location.pathname === '/profile/gallery' && (
                  <ProfileGalleryPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/personal-details' && (
                  <ProfilePersonalDetailsPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/performance-details' && (
                  <ProfilePerformanceDetailsPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/usp' && (
                  <ProfileUspPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/story' && (
                  <ProfileStoryPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/awards-achievements' && (
                  <ProfileAwardsAchievementsPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/past-performances' && (
                  <ProfilePastPerformancesPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/social-media' && (
                  <ProfileSocialMediaPage />
                )}
                {activeNav === 'profile' && location.pathname === '/profile/event-essentials' && (
                  <ProfileEventEssentialsPage />
                )}
                {activeNav === 'jobs' && location.pathname.startsWith('/jobs/chat/') && <ArtistChatPage />}
                {activeNav === 'jobs' && location.pathname.startsWith('/jobs/booking-fill/') && <BookingFillRoute />}
                {activeNav === 'jobs' && location.pathname === '/jobs' && <JobsPage />}
                {activeNav === 'jobs' && /^\/jobs\/\d+$/.test(location.pathname) && <JobDetailPage />}
                {activeNav === 'wallet' && <WalletPage />}
                {activeNav === 'membership' && location.pathname === '/compare-plans' && (
                  <ComparePlansPage />
                )}
                {activeNav === 'membership' && location.pathname !== '/compare-plans' && (
                  <MembershipPage />
                )}
                {activeNav === 'contact' && (
                  <div>
                    <h1 className="text-3xl font-bold mb-4 text-white">Contact Details</h1>
                    <p className="text-[#8E8E93]">Contact details page content</p>
                    {/* Contact details page content will be implemented here */}
                  </div>
                )}
                {activeNav === 'statistics' && <StatisticsPage />}
                {activeNav === 'settings' && <SettingsPage />}
                {activeNav === 'contact-us' && <ContactUsPage />}
                {activeNav === 'faq' && <FAQPage />}
                {activeNav === 'utilities' && (
                  <div>
                    <h1 className="text-3xl font-bold mb-4 text-white">Utilities</h1>
                    <p className="text-[#8E8E93]">Utilities page content</p>
                    {/* Utilities page content will be implemented here */}
                  </div>
                )}
                {activeNav === 'website' && (
                  <div>
                    <h1 className="text-3xl font-bold mb-4 text-white">Preview My Website</h1>
                    <p className="text-[#8E8E93] mb-6">Choose a plan to preview how your website will look.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { id: 'free', label: 'Free', onClick: () => handleWebsiteDesignClick('free'), showMaskedName: true },
                        { id: 'economy', label: 'Economy', onClick: () => handleWebsiteDesignClick('economy'), showMaskedName: true },
                        { id: 'premium', label: 'Premium', onClick: () => handleWebsiteDesignClick('premium') },
                        { id: 'elite', label: 'Elite', onClick: () => handleWebsiteDesignClick('elite') },
                      ].map((item) => (
                        <button
                          key={item.id}
                          onClick={item.onClick}
                          className="flex flex-col items-center justify-center p-6 rounded-xl bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white transition-colors border border-[#3A3A3C]"
                        >
                          <span className="font-semibold">{item.label}</span>
                          <span className="text-xs text-[#8E8E93] mt-1">
                            {item.showMaskedName ? <SparkleMaskedName name={userName} /> : 'Preview'}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </main>
          </div>
        </div>

        {/* Global Search Modal */}
        <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

        {/* Profile photo update modal */}
        <ProfilePhotoModal
          open={isProfilePhotoModalOpen}
          onOpenChange={setIsProfilePhotoModalOpen}
          currentPic={profilePic}
          userName={userName}
        />

        {/* Notifications Sheet */}
        <Sheet
          open={isNotificationsOpen}
          onOpenChange={setIsNotificationsOpen}
          title="Notifications"
          description="View your recent notifications and updates."
        >
          <div className="w-full flex items-center justify-center min-h-[400px]">
            {/* TODO: Replace with actual notifications from API */}
            <div className="flex flex-col items-center justify-center">
              <Bell className="h-12 w-12 text-[#8E8E93] mb-3 opacity-50" />
              <p className="text-[#8E8E93] text-sm text-center">No notifications</p>
              <p className="text-[#8E8E93] text-xs mt-1 text-center">You're all caught up!</p>
            </div>
          </div>
        </Sheet>

        {/* Membership Nudge Sheet (auto-opens; no outside-tap close) */}
        <Sheet
          open={isMembershipNudgeOpen}
          onOpenChange={setIsMembershipNudgeOpen}
          preventOutsideClose
          preventEscapeClose
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <h2 className="text-2xl font-black text-white tracking-tight">
                {membershipNudgeVariant === 'live_free'
                  ? 'Your Website is Live!'
                  : membershipNudgeVariant === 'incomplete_free'
                    ? "Your Profile isn't visible yet!!"
                    : 'Your Website is under review!'}
              </h2>

              {membershipNudgeVariant === 'live_free' && (
                <p className="text-sm text-[#8E8E93] font-medium leading-relaxed">
                  Unlock its full potential by becoming a member to access exclusive features.
                </p>
              )}

              {membershipNudgeVariant === 'incomplete_free' && (
                <p className="text-sm text-[#8E8E93] font-medium leading-relaxed">
                  Busy schedule? Let us help you complete it and enjoy early access to booking with
                  membership.
                </p>
              )}

              {membershipNudgeVariant === 'on_review' && (
                <div className="space-y-2">
                  <p className="text-sm text-[#8E8E93] font-medium leading-relaxed">
                    You&apos;re{' '}
                    <span className="text-white font-bold">
                      #
                      {queueNumberDisplay !== null ? queueNumberDisplay : '--'}
                    </span>{' '}
                    on the waitlist.
                  </p>
                  <p className="text-sm text-[#8E8E93] font-medium leading-relaxed">
                    Want to skip the line and unlock exclusive advantages? See what our members get.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Button
                variant="active"
                className="w-full py-4 text-base font-bold rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] active:bg-[#D92D58]"
                onClick={() => {
                  setIsMembershipNudgeOpen(false);
                  navigate('/membership');
                }}
              >
                UPGRADE NOW
              </Button>

              {membershipNudgeVariant === 'incomplete_free' && (
                <button
                  type="button"
                  className="w-full py-3 text-sm font-semibold text-[#E5E5EA] hover:text-white transition-colors"
                  onClick={() => setIsMembershipNudgeOpen(false)}
                >
                  CONTINUE FOR FREE
                </button>
              )}
            </div>
          </div>
        </Sheet>

        {/* Mobile Sidebar Sheet - Slides from Left with Blur */}
        <LeftSheet
          open={isMobileSidebarOpen}
          onOpenChange={setIsMobileSidebarOpen}
        >
          {/* Profile Section */}
          <div className="px-8 pb-4 pt-10  relative">
            {/* Live Indicator */}
            {isLive && (
              <div className="absolute top-8 right-14 flex items-center gap-1.5 px-2 py-1 bg-[#F33363]/10 rounded-full border border-[#F33363]/20">
                <div className="relative">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#F33363]" />
                  <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-[#F33363] animate-ping opacity-75" />
                </div>
                <span className="text-[9px] text-[#F33363] font-black uppercase tracking-wider">Live</span>
              </div>
            )}

            <div className="flex flex-col items-center">
              <div className="relative mb-6">
                <ProfilePicWithProgress
                  src={profilePic}
                  alt={userName}
                  percent={fullProfilePercent}
                  size="md"
                  showLabelAndBar
                />
              </div>

              <div className="text-center space-y-1">
                <h2 className="text-xl font-black text-white tracking-tight">{userName}</h2>
                {artistCategoryName ? (
                  <p className="text-xs text-[#8E8E93] font-medium uppercase tracking-wide">{artistCategoryName}</p>
                ) : null}
                <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full">
                  <span className="text-[10px] font-black text-[#E5E5EA] uppercase tracking-widest leading-tight text-center">
                    <span className="block">Plan: {planDisplayName}</span>
                    <span className="block">
                      Status:{' '}
                      <span className={planBadgeStatusClass}>
                        {profileStatus}
                      </span>
                    </span>
                    {subscriptionExpireLabel ? (
                      <span className="block">
                        Expire :{' '}
                        <span className="text-white">{subscriptionExpireLabel}</span>
                      </span>
                    ) : null}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-center">
              <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#3A3A3C] to-transparent" />
            </div>
          </div>

          {/* Navigation - Mobile: Back to Agency above; then Home, Profile, Statistics, Contact Details; gray area = Settings, Contact Us, FAQ */}
          <nav className="flex-1 overflow-y-auto w-full flex flex-col min-h-0 pb-6">
            {/* Back to Agency (agency users only) - above the Home/Profile/etc block */}
            {isAgencyUser && (
              <div className="w-full px-4 my-2">
                <button
                  type="button"
                  onClick={() => {
                    navigate('/agency-home');
                    setIsMobileSidebarOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-1.5 rounded-lg transition-colors text-white hover:bg-[#2C2C2E]"
                >
                  <Building2 className="h-5 w-5" />
                  <span className="text-sm font-medium">Back to Agency</span>
                </button>
              </div>
            )}
            <div className="w-64 pl-2 py-1 bg-[#0B0B0D] rounded-lg ml-4">
              <button
                onClick={() => {
                  setIsMobileSidebarOpen(false);
                  setIsWebsitePlanSheetOpen(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[#2C2C2E] text-white/40 transition-colors duration-200 ease-out"
              >
                <Sparkles className="h-5 w-5" />
                <span className="text-sm font-medium transition-colors duration-200 ease-out">
                  Preview My Website
                </span>
              </button>
            </div>
            {/* Major Navigation Items (Home, Profile, Statistics, Contact Details) */}
            <div className="w-64 pl-2 py-2 bg-[#0B0B0D] rounded-lg ml-4 my-2 space-y-1">
              {mobileMajorNavItems.map((item) => {
                const isOnContactDetailsTab = location.pathname === '/profile/event-essentials' && searchParams.get('tab') === 'contact-details';
                const isContactDetailsActive = item.id === 'contact' && isOnContactDetailsTab;
                // When on Contact Details tab, only highlight Contact Details; don't also highlight Profile
                const isProfileActive = item.id === 'profile' && activeNav === 'profile' && !isOnContactDetailsTab;
                const isActive = item.id === 'contact' ? isContactDetailsActive : item.id === 'profile' ? isProfileActive : activeNav === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.id === 'contact') {
                        navigate('/profile/event-essentials?tab=contact-details');
                      } else {
                        handleNavClick(item);
                      }
                      setIsMobileSidebarOpen(false);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors duration-200 ease-out text-white/40 font-normal',
                      'hover:bg-[#2C2C2E]',
                      isActive && 'bg-[#2C2C2E] border-r-2 border-[#F33363] text-white font-bold'
                    )}
                  >
                    {item.icon}
                    <span className="text-sm transition-colors duration-200 ease-out">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Gray area: Settings, Contact Us, FAQ */}
            <div className="w-full px-4  rounded-lg space-y-1">
              <button
                onClick={() => {
                  handleUtilityClick('settings');
                  setIsMobileSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[#2C2C2E] text-white/40 transition-colors duration-200 ease-out"
              >
                <Settings className="h-5 w-5" />
                <span className="text-sm font-medium transition-colors duration-200 ease-out">
                  Settings
                </span>
              </button>
              <button
                onClick={() => {
                  handleUtilityClick('contact-us');
                  setIsMobileSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[#2C2C2E] text-white/40 transition-colors duration-200 ease-out"
              >
                <Mail className="h-5 w-5" />
                <span className="text-sm font-medium transition-colors duration-200 ease-out">
                  Contact Us
                </span>
              </button>
              <button
                onClick={() => {
                  handleUtilityClick('faq');
                  setIsMobileSidebarOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-[#2C2C2E] text-white/40 transition-colors duration-200 ease-out"
              >
                <HelpCircle className="h-5 w-5" />
                <span className="text-sm font-medium transition-colors duration-200 ease-out">
                  FAQ
                </span>
              </button>
            </div>

            {/* Logout Button */}
            <div className="p-4 mt-4">
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileSidebarOpen(false);
                }}
                className="w-full px-4 py-2.5 rounded-lg bg-[#2C2C2E] hover:bg-[#2C2C2E] text-white text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </nav>
        </LeftSheet>

        <Sheet
          open={isWebsitePlanSheetOpen}
          onOpenChange={setIsWebsitePlanSheetOpen}
          title="Preview My Website"
          description="Choose a plan to preview your website design."
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { id: 'free', label: 'Free' },
              { id: 'economy', label: 'Economy' },
              { id: 'premium', label: 'Premium' },
              { id: 'elite', label: 'Elite' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setIsWebsitePlanSheetOpen(false);
                  handleWebsiteDesignClick(item.id);
                }}
                className="flex flex-col items-center justify-center p-5 rounded-xl bg-[#2C2C2E] hover:bg-[#3A3A3C] text-white transition-colors border border-[#3A3A3C]"
              >
                <span className="font-semibold">{item.label}</span>
                <span className="text-xs text-[#8E8E93] mt-1">Preview</span>
              </button>
            ))}
          </div>
        </Sheet>

        {/* Primary Contact Edit Sheet */}
        <Sheet
          open={isPrimaryContactEditOpen}
          onOpenChange={setIsPrimaryContactEditOpen}
          title="Edit Primary Contact"
          description="Update your primary contact information"
        >
          <div className="space-y-6">
            {/* Tabs */}
            <div className="flex w-fit justify-center items-center gap-2 p-1 bg-[#0B0B0D] rounded-xl">
              <button
                onClick={() => setActiveContactTab('email')}
                className={cn(
                  ' px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeContactTab === 'email'
                    ? 'bg-[#2C2C2E] text-white shadow-lg'
                    : 'text-[#8E8E93] hover:text-white'
                )}
              >
                Update Email
              </button>
              <button
                onClick={() => setActiveContactTab('phone')}
                className={cn(
                  ' px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeContactTab === 'phone'
                    ? 'bg-[#2C2C2E] text-white shadow-lg'
                    : 'text-[#8E8E93] hover:text-white'
                )}
              >
                Update Phone
              </button>
            </div>

            {/* Tab Content */}
            {activeContactTab === 'email' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Current Email</label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C]">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-[#8E8E93]">{contactEmail}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">New Email</label>
                  <input
                    type="email"
                    placeholder="Enter new email address"
                    className="w-full px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C] text-white placeholder:text-[#8E8E93] focus:outline-none focus:border-[#F33363] transition-colors"
                  />
                </div>
                <Button
                  variant="active"
                  className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wider"
                >
                  Update Email
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Current Phone</label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C]">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-[#8E8E93]">{contactPhone}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">New Phone</label>
                  <input
                    type="tel"
                    placeholder="Enter new phone number"
                    className="w-full px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C] text-white placeholder:text-[#8E8E93] focus:outline-none focus:border-[#F33363] transition-colors"
                  />
                </div>
                <Button
                  variant="active"
                  className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wider"
                >
                  Update Phone
                </Button>
              </div>
            )}
          </div>
        </Sheet>

        {/* Secondary Contact Edit Sheet */}
        <Sheet
          open={isSecondaryContactEditOpen}
          onOpenChange={setIsSecondaryContactEditOpen}
          title="Edit Secondary Contact"
          description="Update your secondary contact information"
        >
          <div className="space-y-6">
            {/* Tabs */}
            <div className="flex w-fit justify-center items-center gap-2 p-1 bg-[#0B0B0D] rounded-xl">
              <button
                onClick={() => setActiveContactTab('email')}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeContactTab === 'email'
                    ? 'bg-[#2C2C2E] text-white shadow-lg'
                    : 'text-[#8E8E93] hover:text-white'
                )}
              >
                Update Email
              </button>
              <button
                onClick={() => setActiveContactTab('phone')}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                  activeContactTab === 'phone'
                    ? 'bg-[#2C2C2E] text-white shadow-lg'
                    : 'text-[#8E8E93] hover:text-white'
                )}
              >
                Update Phone
              </button>
            </div>

            {/* Tab Content */}
            {activeContactTab === 'email' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Current Email</label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C]">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-[#8E8E93]">secondary@starclinch.com</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">New Email</label>
                  <input
                    type="email"
                    placeholder="Enter new email address"
                    className="w-full px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C] text-white placeholder:text-[#8E8E93] focus:outline-none focus:border-[#F33363] transition-colors"
                  />
                </div>
                <Button
                  variant="active"
                  className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wider"
                >
                  Update Email
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">Current Phone</label>
                  <div className="flex items-center gap-2 px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C]">
                    <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="text-sm text-[#8E8E93]">+91 98765 12345</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white mb-2">New Phone</label>
                  <input
                    type="tel"
                    placeholder="Enter new phone number"
                    className="w-full px-4 py-3 bg-[#0B0B0D] rounded-lg border border-[#3A3A3C] text-white placeholder:text-[#8E8E93] focus:outline-none focus:border-[#F33363] transition-colors"
                  />
                </div>
                <Button
                  variant="active"
                  className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wider"
                >
                  Update Phone
                </Button>
              </div>
            )}
          </div>
        </Sheet>

        {/* Global assistant mini-chat – persists across protected routes within UserHome (except on full assistant page) */}
        {/* {location.pathname !== '/assistant' && (
          <AssistantMiniChat uiContext={assistantUiContext} />
        )} */}

        {activeExpressEvent && !isKeyboardOpen ? (
          <div
            className="fixed inset-x-0 z-[56] px-3 lg:hidden"
            style={{
              bottom: `${ongoingEventStripBottom}px`,
            }}
          >
            <button
              type="button"
              className="event-ongoing-strip-glass mx-auto flex w-full max-w-[560px] items-center justify-between rounded-2xl border border-[#F33363]/50 px-4 py-3 text-left shadow-[0_12px_32px_rgba(0,0,0,0.55)]"
              onClick={() =>
                navigate('/jobs', {
                  state: { tab: 'express', spotlightBookingDealId: activeExpressEvent.dealId },
                })
              }
            >
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-white uppercase tracking-wider">
                <Clock3 className="h-4 w-4 text-[#FFD3E0]" />
                Event ongoing for {activeExpressEvent.elapsedMinutes} minute
                {activeExpressEvent.elapsedMinutes === 1 ? '' : 's'}
              </span>
              <span className="rounded-full bg-black/20 px-3 py-1 text-[11px] font-bold tracking-wider text-[#FFE5EE]">
                {ongoingEventCountdown}
              </span>
            </button>
          </div>
        ) : null}

        {/* Bottom Navigation - Mobile Only */}
        {!isKeyboardOpen && <BottomNav />}
      </div>
    </>
  );
};
